import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { apiFetch } from '../api';

// ── Claude API ────────────────────────────────────────────────────────────────
// Auth (x-api-key + anthropic-version) is injected by the api-proxy
// Lambda — never sent from the browser. See api-proxy/src/index.mjs.
// Default (Strategy chat, Home chat, analysis, classification, descriptions)
const CLAUDE_MODEL           = 'claude-sonnet-4-6';
// Used only for the onboarding → strategy bootstrap, where accurately extracting
// four strategy parameters from a free-form transcript is high-stakes.
const CLAUDE_MODEL_REASONING = 'claude-opus-4-7';
// Fast/cheap model for short summaries and news-impact analysis (Markets & Context).
const CLAUDE_MODEL_FAST      = 'claude-haiku-4-5';

// ── Finnhub (market news) ─────────────────────────────────────────────────────
// Token is injected by the api-proxy Lambda (FINNHUB_API_KEY env var
// sourced from Doppler) — no key in the browser bundle.

// ── HeyGen LiveAvatar (onboarding video face, ElevenLabs Agent connector) ───
// LiveAvatar runs the agent server-side: it bridges to ElevenLabs Convai
// (ASR + LLM + TTS) and renders the lip-synced avatar video over LiveKit.
// Browser flow is purely: mint a session_token → render the SDK provider.
// Auth (X-API-KEY) is injected by the api-proxy Lambda.
//
// The Convai key is registered on demand via /api/register-tts-secret with
// purpose='convai' and the returned secret_id is cached in localStorage.
// Storing a hardcoded id here would break the moment the key is rotated,
// since HeyGen keeps the value alongside the id.

// Roster of available onboarding advisors. Each has its own ElevenLabs
// Convai agent (different personality + voice) paired with a LiveAvatar
// avatar (different face). The pre-session carousel lets the user pick.
// Append entries here to expand the choice; order is the carousel order.
//
// chromakey: set to true ONLY for avatars rendered against a pure-green
// background. The WebGL keyer drops green to transparent. Avatars rendered
// against a real studio scene should stay false.
const ADVISORS = [
  {
    id:        'katya',
    name:      'Katya',
    agentId:   'agent_3601kphk2xdjem6sb50jrtercap2',
    avatarId:  '073b60a9-89a8-45aa-8902-c358f64d2852',
    chromakey: false,
  },
  {
    id:        'wayne',
    name:      'Wayne',
    agentId:   'agent_6501kqd6fbb6fdermvjt0gqxk4kg',
    avatarId:  'dd73ea75-1218-4ef3-92ce-606d5f7fbc0a',
    chromakey: false,
  },
  {
    id:        'shawn',
    name:      'Shawn',
    agentId:   'agent_4601kqd7secte0aa5tngzt9bc0xf',
    avatarId:  '7b888024-f8c9-4205-95e1-78ce01497bda',
    chromakey: false,
  },
  {
    id:        'dexter',
    name:      'Dexter',
    agentId:   'agent_8301kqd8r2p5f6kr1xnqdpkyj9xq',
    avatarId:  '0930fd59-c8ad-434d-ad53-b391a1768720',
    chromakey: false,
  },
  {
    id:        'santa',
    name:      'Santa Claus',
    agentId:   'agent_3701kqd91207f4kr8x899a2ykg6h',
    avatarId:  '1c690fe7-23e0-49f9-bfba-14344450285b',
    chromakey: false,
  },
];

// ── ElevenLabs Text-to-Speech (for Strategy / Home pages) ─────────────────────
// Used as the *fallback* TTS path when the chat-mode LiveAvatar isn't
// connected yet (warm-up or token mint failure). Auth (xi-api-key) is
// injected by the api-proxy Lambda — TTS-scope key for non-Convai paths,
// Convai-scope key for /v1/convai/* endpoints. Neither key lives in
// the browser bundle.
const EL_VOICE_ID = 'dMyQqiVXTU80dDl2eNK8'; // Eryn — fallback when an advisor's voice can't be resolved
const EL_MODEL    = 'eleven_flash_v2_5';

// Selects which provider answers ask_market questions. 'gemini' uses Gemini
// 2.5 Pro with Google Search grounding; 'claude' uses Claude Sonnet 4.6 with
// Anthropic's web_search_20250305 server-side tool. Set via .env, defaults to
// 'gemini'. Restart the dev server after changing (CRA bakes env at build time).
const MARKET_AGENT_PROVIDER = (process.env.REACT_APP_MARKET_AGENT_PROVIDER || 'gemini').toLowerCase();

// ── Persistent memory ────────────────────────────────────────────────────
// Single localStorage key holds a JSON digest that lets the app feel like a
// continuing relationship — the agent picks up where the last session left
// off, references stated concerns, follows through on flagged items.
// Production version of this would live on a backend; the in-context shape
// we define here ports cleanly.
const MEMORY_KEY = 'puro-wealth-memory';
const MEMORY_VERSION = 1;

function loadMemory() {
  try {
    const raw = localStorage.getItem(MEMORY_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw);
    if (!m || typeof m !== 'object' || m.version !== MEMORY_VERSION) return null;
    return m;
  } catch { return null; }
}

function saveMemory(m) {
  try { localStorage.setItem(MEMORY_KEY, JSON.stringify(m)); } catch {}
}

function clearMemoryLS() {
  try { localStorage.removeItem(MEMORY_KEY); } catch {}
}

// Seeded "yesterday's session" used by the demo Seed button so reviewers can
// land directly on the day-after experience without having to run onboarding
// first. Mirrors a plausible first-session outcome — Balanced + 3pp gold,
// $10k allocated, surfaced inflation concerns.
function buildSeedMemory() {
  const yesterday = new Date(Date.now() - 24 * 3600 * 1000);
  return {
    version: MEMORY_VERSION,
    firstSession: yesterday.toISOString(),
    lastSession:  yesterday.toISOString(),
    sessionCount: 1,
    selectedAdvisor: 'katya',
    riskTolerance: 'balanced',
    horizon: 'long',
    goals: ['retirement in ~25 years'],
    profile: 'balanced',
    tilts: { shortPercent: 0, goldPercent: 3, bondsPercent: 0 },
    allocationUsd: 10000,
    concerns: ['inflation', 'tech valuations'],
    preferences: ['likes gold as inflation hedge', 'comfortable with moderate leverage'],
    recentSessions: [{
      date: yesterday.toISOString().slice(0, 10),
      summary: 'Set up a Balanced strategy with +3pp gold tilt and allocated $10,000. Discussed McKesson as the largest long position and the role of gold as an inflation hedge. Briefly considered Aggressive but stayed with Balanced for now.',
      followUps: ['Revisit gold allocation if Fed signals rate cuts', 'Curious about how the strategy holds up in a recession'],
    }],
    olderHistorySummary: '',
  };
}

// Compact prose digest of memory, injected into the agent's system prompts
// so the model can reference continuity naturally without dedicated tools.
function buildMemoryContext(m) {
  if (!m) return '';
  const lines = ['', 'USER MEMORY (continuity from prior sessions — reference naturally where relevant; do not over-cite):'];
  const first = m.firstSession?.slice(0, 10);
  const last  = m.lastSession?.slice(0, 10);
  lines.push(`  • Returning user. First session ${first || 'unknown'}, most recent ${last || 'unknown'} (${m.sessionCount || 1} session${(m.sessionCount || 1) > 1 ? 's' : ''} total).`);
  if (m.selectedAdvisor) lines.push(`  • Working with advisor: ${m.selectedAdvisor}.`);
  if (m.profile) {
    const t = m.tilts || {};
    const tiltStr = (Math.abs(t.shortPercent || 0) + Math.abs(t.goldPercent || 0) + Math.abs(t.bondsPercent || 0)) > 0
      ? ` (tilts: short ${t.shortPercent || 0}pp, gold ${t.goldPercent || 0}pp, bonds ${t.bondsPercent || 0}pp)`
      : '';
    lines.push(`  • Deployed strategy: ${m.profile} profile${tiltStr}.`);
  }
  if (m.allocationUsd) lines.push(`  • Allocated capital: $${Number(m.allocationUsd).toLocaleString()}.`);
  if (m.goals?.length)       lines.push(`  • Stated goals: ${m.goals.join('; ')}.`);
  if (m.concerns?.length)    lines.push(`  • Concerns the user has raised: ${m.concerns.join('; ')}.`);
  if (m.preferences?.length) lines.push(`  • Stated preferences: ${m.preferences.join('; ')}.`);
  if (m.recentSessions?.length) {
    lines.push(`  • Recent sessions:`);
    for (const s of m.recentSessions.slice(-3)) {
      const fu = s.followUps?.length ? ` Follow-ups flagged: ${s.followUps.join('; ')}.` : '';
      lines.push(`      ${s.date}: ${s.summary}${fu}`);
    }
  }
  if (m.olderHistorySummary) lines.push(`  • Older history: ${m.olderHistorySummary}`);
  return lines.join('\n');
}

// ── Config + CSV loaders ──────────────────────────────────────────────────────

async function loadSleeveData() {
  const res  = await fetch('/prototype_data.csv');
  const text = await res.text();
  const { data } = Papa.parse(text, { header: true, skipEmptyLines: true });
  return data
    .map(row => ({
      date:         row['date']?.trim(),
      long_sleeve:  parseFloat(row['long_sleeve'])  / 100,
      short_sleeve: parseFloat(row['short_sleeve']) / 100,
      gold_sleeve:  parseFloat(row['gold_sleeve'])  / 100,
      bonds_sleeve: parseFloat(row['bonds_sleeve']) / 100,
      sp500:        parseFloat(row['S&P 500'])       / 100,
    }))
    .filter(row => {
      const d = new Date(row.date);
      return !isNaN(d) && d >= new Date('2010-01-01');
    });
}

async function loadConfig() {
  const res = await fetch('/advisor-config.json');
  return res.json();
}

// ── Claude API helper ─────────────────────────────────────────────────────────

// Anthropic occasionally returns 529 (overloaded) during traffic spikes —
// it's a transient service-side capacity issue, not an account throttle.
// 429 is rate-limiting; same retry treatment makes the app resilient to
// brief bursts. Three attempts with exponential backoff (1s → 2s → 4s)
// usually clears it; if not, the error bubbles up as before.
async function fetchClaudeWithRetry(body) {
  const RETRY_STATUSES = new Set([429, 500, 502, 503, 504, 529]);
  const MAX_ATTEMPTS = 3;
  let lastErr;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    let res;
    try {
      res = await apiFetch('/api/claude', {
        method: 'POST',
        headers: {
          // Auth (x-api-key + anthropic-version) is injected by the
          // api-proxy Lambda from its own env vars — never from the
          // client. The key is not in the browser bundle.
          'content-type': 'application/json',
        },
        body: JSON.stringify(body),
      });
    } catch (netErr) {
      // Network blip — same retry policy.
      lastErr = netErr;
      if (attempt === MAX_ATTEMPTS - 1) throw netErr;
      await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
      continue;
    }
    if (res.ok) return res;
    if (!RETRY_STATUSES.has(res.status) || attempt === MAX_ATTEMPTS - 1) {
      const err = await res.text();
      throw new Error(`Claude ${res.status}: ${err}`);
    }
    // Retryable status — read body for logs, then back off.
    const errBody = await res.text();
    console.warn('[claude] %d retryable, attempt %d/%d: %s', res.status, attempt + 1, MAX_ATTEMPTS, errBody.slice(0, 160));
    await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
  }
  throw lastErr || new Error('Claude request failed after retries');
}

async function callClaude(systemPrompt, claudeMessages, tools = null, model = CLAUDE_MODEL, maxTokens = 1024) {
  const body = {
    model,
    max_tokens: maxTokens,
    system: systemPrompt,
    messages: claudeMessages,
  };
  if (tools && tools.length > 0) {
    body.tools = tools;
    body.tool_choice = tools.length === 1
      ? { type: 'tool', name: tools[0].name }
      : { type: 'any' };
  }
  const res = await fetchClaudeWithRetry(body);
  const data = await res.json();
  if (tools) {
    // A max_tokens cutoff mid-tool-call leaves `input` truncated / malformed,
    // which silently round-trips as an empty / invalid tool_use. Surface it
    // as an error so callers can bump maxTokens and don't loop on empty output.
    if (data.stop_reason === 'max_tokens') {
      throw new Error(`Claude hit max_tokens (${maxTokens}); tool response was truncated.`);
    }
    const toolUse = data.content.find(c => c.type === 'tool_use');
    if (!toolUse) throw new Error('Claude did not call a tool');
    return { toolName: toolUse.name, input: toolUse.input };
  }
  return data.content[0].text;
}

// Multi-turn variant of callClaude: returns the raw API response so the caller
// can run a tool-execution loop (e.g. data-fetch tools followed by a terminal
// respond_to_client). Accepts `tool_choice` to support `auto` (used in loops
// where the model may choose to respond with text once data is fetched).
async function callClaudeMulti(systemPrompt, claudeMessages, tools, model = CLAUDE_MODEL, maxTokens = 4096, toolChoice = null) {
  const body = {
    model,
    max_tokens: maxTokens,
    system: systemPrompt,
    messages: claudeMessages,
  };
  if (tools && tools.length > 0) {
    body.tools = tools;
    body.tool_choice = toolChoice
      || (tools.length === 1 ? { type: 'tool', name: tools[0].name } : { type: 'any' });
  }
  const res = await fetchClaudeWithRetry(body);
  return await res.json();
}

// ── Risk profiles + tilts ────────────────────────────────────────────────
// The Strategy page presents these four named profiles as the primary
// control. The agent recommends a profile + optional tilts during onboarding
// rather than negotiating raw parameters; raw numbers remain available as a
// power-user escape hatch.
const RISK_PROFILES = {
  conservative: { name: 'Conservative', grossExposure: 75,  shortPercent: 12, goldPercent: 12, bondsPercent: 30 },
  balanced:     { name: 'Balanced',     grossExposure: 105, shortPercent: 18, goldPercent: 8,  bondsPercent: 18 },
  growth:       { name: 'Growth',       grossExposure: 130, shortPercent: 22, goldPercent: 5,  bondsPercent: 10 },
  aggressive:   { name: 'Aggressive',   grossExposure: 150, shortPercent: 27, goldPercent: 3,  bondsPercent: 5  },
};
const PROFILE_KEYS = ['conservative', 'balanced', 'growth', 'aggressive'];
const ZERO_TILTS = { shortPercent: 0, goldPercent: 0, bondsPercent: 0 };

const _clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// Apply tilts (each in pp, range roughly ±10) on top of a profile preset.
// Bounds enforced; if the non-long sleeves would exceed 95% combined, scale
// them down proportionally so a long residual remains.
function paramsFromProfileAndTilts(profileKey, tilts = ZERO_TILTS) {
  const preset = RISK_PROFILES[profileKey];
  if (!preset) return null;
  let sh = _clamp(preset.shortPercent + (tilts.shortPercent || 0), 0, 50);
  let go = _clamp(preset.goldPercent  + (tilts.goldPercent  || 0), 0, 30);
  let bo = _clamp(preset.bondsPercent + (tilts.bondsPercent || 0), 0, 50);
  const total = sh + go + bo;
  if (total > 95) {
    const factor = 95 / total;
    sh = sh * factor; go = go * factor; bo = bo * factor;
  }
  return {
    grossExposure: preset.grossExposure,
    shortPercent:  Math.round(sh),
    goldPercent:   Math.round(go),
    bondsPercent:  Math.round(bo),
  };
}

// Inverse: given a params bundle, return the closest profile (used to label
// the dial when the agent passes raw numbers via the power-user escape).
function findClosestProfile(params) {
  if (!params) return null;
  let best = null, bestScore = Infinity;
  for (const key of PROFILE_KEYS) {
    const p = RISK_PROFILES[key];
    const score =
      Math.pow(params.grossExposure - p.grossExposure, 2) * 2 +
      Math.pow(params.shortPercent  - p.shortPercent,  2) +
      Math.pow(params.goldPercent   - p.goldPercent,   2) +
      Math.pow(params.bondsPercent  - p.bondsPercent,  2);
    if (score < bestScore) { best = key; bestScore = score; }
  }
  return best;
}

function tiltsFromParams(profileKey, params) {
  const preset = RISK_PROFILES[profileKey];
  if (!preset || !params) return { ...ZERO_TILTS };
  return {
    shortPercent: params.shortPercent - preset.shortPercent,
    goldPercent:  params.goldPercent  - preset.goldPercent,
    bondsPercent: params.bondsPercent - preset.bondsPercent,
  };
}

const ADVISOR_TOOL = {
  name: 'respond_to_client',
  description: 'Respond to the client. The PREFERRED way to set or change the strategy is via `profile` (one of conservative/balanced/growth/aggressive) and optional `tilts` — this is the primary control surface and matches how the Strategy page is presented. Raw numeric fields (grossExposure / shortPercent / goldPercent / bondsPercent) are a power-user escape hatch; only use them when the client explicitly asks for specific percentages. When just asking questions, acknowledging input, or answering queries about the existing strategy, omit ALL of profile / tilts / raw fields.',
  input_schema: {
    type: 'object',
    properties: {
      message: { type: 'string', description: 'Your conversational response to the client (2–4 sentences, warm and professional). ALWAYS end with a question that invites the client\'s next action.' },

      // Preferred control surface
      profile: {
        type: 'string',
        enum: PROFILE_KEYS,
        description: 'Risk profile preset. conservative=most cautious; balanced=middle ground; growth=growth-tilted; aggressive=highest risk/reward. Setting a profile resets tilts to zero. Pick one when finalizing the initial strategy or shifting the client to a different overall risk level.',
      },
      tilts: {
        type: 'object',
        description: 'Composition adjustments WITHIN the current profile. Each value is an absolute pp delta from the profile baseline (range -10 to +10), NOT cumulative across calls. Example: tilts: { goldPercent: 3 } means "set the gold tilt to +3 from baseline" (a "more gold" nudge). Omit this object when not adjusting tilts. If you set both profile and tilts in the same call, tilts apply to the new profile.',
        properties: {
          shortPercent: { type: 'number', description: 'Drop-hedge tilt in pp (-10 to +10). Negative = less drop hedge.' },
          goldPercent:  { type: 'number', description: 'Gold tilt in pp (-10 to +10).' },
          bondsPercent: { type: 'number', description: 'Bonds tilt in pp (-10 to +10).' },
        },
      },

      // Power-user escape — use ONLY when the client explicitly asks for specific percentages
      grossExposure: { type: 'number', description: 'POWER-USER ESCAPE only. Total invested %, 0–200. Use only when the client explicitly asks for a specific number; otherwise pick a profile. Setting any raw number flips the UI to "Custom" mode.' },
      shortPercent:  { type: 'number', description: 'POWER-USER ESCAPE only. Drop-hedge weight, 0–50.' },
      goldPercent:   { type: 'number', description: 'POWER-USER ESCAPE only. Gold weight, 0–30.' },
      bondsPercent:  { type: 'number', description: 'POWER-USER ESCAPE only. Bonds weight, 0–50.' },
    },
    required: ['message'],
  },
};

const DEPLOY_STRATEGY_TOOL = {
  name: 'deploy_strategy',
  description: 'Queue the current strategy for deployment to the client\'s live portfolio. Call this ONLY after BOTH (1) the client has explicitly confirmed they are ready to deploy ("yes, deploy it", "let\'s go live", "I\'m ready", "confirm", "go ahead"), AND (2) the client has stated how much they want to allocate to this strategy. If the client confirms deployment but has not yet specified an allocation, do NOT call this tool — call respond_to_client first to ask "How much would you like to allocate to this strategy?" Only call deploy_strategy once you have the allocation number. Behind the scenes the strategy goes live immediately and populates the Home dashboard, but the message you send the client should frame it as queued for portfolio-team review (see message description).',
  input_schema: {
    type: 'object',
    properties: {
      allocationUsd: {
        type: 'number',
        description: 'The dollar amount the client wants to allocate to this strategy, parsed from their reply. Convert "$10,000", "10k", "ten thousand", etc. to a plain number (10000). Required.',
      },
      message: {
        type: 'string',
        description: 'Your wrap-up confirmation to the client. 2–3 sentences, warm and professional. (1) State the allocation amount and confirm the strategy has been queued for review and deployment by the portfolio management team — do NOT say it\'s deployed instantly, frame it as queued for review. (2) Suggest they switch to the Home tab where they can explore current holdings, track performance over time, and monitor what\'s driving the portfolio day-to-day. (3) IMPORTANT: this is the END of the conversation. Do NOT end with a question, do NOT ask if they need anything else, do NOT invite further interaction — the mic closes after this message and the user is meant to navigate to Home. End on a confident, definitive note (e.g. "Your strategy is on its way." or "Talk soon — until then, the Home dashboard is your window into how it\'s performing.").',
      },
    },
    required: ['allocationUsd', 'message'],
  },
};

const SHOW_HOLDINGS_TOOL = {
  name: 'show_holdings',
  description: 'Display individual portfolio positions (long and short baskets) for a specific date. ONLY call when the most recent user message EXPLICITLY asks to see specific stocks, holdings, or positions (e.g. "what do we hold?", "show me the positions", "what stocks are in this?"). Do NOT call for follow-up turns where the user is responding about something else (deployment, allocation amount, confirmation, sign-off) — even if holdings were shown earlier. Once the modal has been shown, assume the client has the information until they ask again. For CURRENT/PRESENT/TODAY requests, set isCurrent=true and pass today\'s date. For HISTORICAL requests ("what did we hold in March 2015?"), omit isCurrent (or set false) and pass the historical date.',
  input_schema: {
    type: 'object',
    properties: {
      date:      { type: 'string',  description: 'ISO date YYYY-MM-DD. For current/today requests, pass today\'s date. For historical, pass the requested date.' },
      isCurrent: { type: 'boolean', description: 'True when the client is asking about current/present/today\'s holdings. False or omitted for historical dates.' },
    },
    required: ['date'],
  },
};

const SHOW_PERFORMANCE_TOOL = {
  name: 'show_performance',
  description: 'Display a chart comparing the strategy\'s growth vs. the S&P 500 benchmark over a specific historical date range ($10,000 invested at the start of the range, both rebased). ONLY call when the most recent user message EXPLICITLY asks about performance over a named period (e.g. "how did it do during COVID?", "performance in 2022", "last 5 years", "during the 2018 correction"). Do NOT call for follow-up turns where the user is responding about something else (deployment, allocation amount, confirmation, sign-off, holdings, sectors). Once shown, assume the client has the information until they ask again. You must decide whether the client\'s request uniquely identifies a date range. If yes, pick sensible start/end dates based on the conversation context (e.g. "during COVID" → fromDate 2020-02-15, toDate 2022-12-31; "in 2022" → 2022-01-01 to 2022-12-31; "last 3 years" → use today\'s date minus 3 years to today). If the request is genuinely vague ("recently", "during the bad years") and you cannot confidently pick a range, call respond_to_client instead and ask the client to clarify the period. Data is available from roughly Jan 2010 through Apr 2026.',
  input_schema: {
    type: 'object',
    properties: {
      fromDate: { type: 'string', description: 'Start of the period, ISO YYYY-MM-DD.' },
      toDate:   { type: 'string', description: 'End of the period, ISO YYYY-MM-DD.' },
    },
    required: ['fromDate', 'toDate'],
  },
};

const SHOW_SECTORS_TOOL = {
  name: 'show_sectors',
  description: 'Display the portfolio sector mix as two donut charts (long and short sides) for a specific date. ONLY call when the most recent user message EXPLICITLY asks about sector exposure, sector mix, industry breakdown, or how the portfolio is allocated across sectors. Do NOT call for follow-up turns where the user is responding about something else (deployment, allocation amount, confirmation, sign-off, holdings, performance) — even if sectors were shown earlier. Once the modal has been shown, assume the client has the information until they ask again. Prefer this over show_holdings when the client is explicitly asking about sectors/industries rather than individual positions. For CURRENT/TODAY requests, set isCurrent=true and pass today\'s date. For HISTORICAL requests, omit isCurrent and pass the historical date.',
  input_schema: {
    type: 'object',
    properties: {
      date:      { type: 'string',  description: 'ISO date YYYY-MM-DD. For current/today requests, pass today\'s date. For historical, pass the requested date.' },
      isCurrent: { type: 'boolean', description: 'True when the client is asking about current/present/today\'s sector mix. False or omitted for historical dates.' },
    },
    required: ['date'],
  },
};

const ANALYZE_THEMES_TOOL = {
  name: 'analyze_portfolio_themes',
  description: 'Distill today\'s market news flow into 6–10 cross-cutting themes and score each one\'s net effect on the client\'s specific portfolio. Each theme is bucketed on two axes: polarity (positive vs. negative for THIS portfolio) and magnitude (major vs. minor impact). A continuous intensity score (0–1) gives graduated emphasis used for pill-sizing in the UI.',
  input_schema: {
    type: 'object',
    properties: {
      themes: {
        type: 'array',
        description: '6–10 distinct themes. Prefer cross-cutting narratives (e.g. "Rising oil prices", "Fed rate cuts", "AI capex boom") over single-headline restatements.',
        items: {
          type: 'object',
          properties: {
            name:      { type: 'string', description: 'Short theme label, 2–5 words — e.g. "Rising oil prices". Title-case-ish. No tickers.' },
            polarity:  { type: 'string', enum: ['positive', 'negative'], description: 'Net effect on THIS portfolio (not on the market generally).' },
            magnitude: { type: 'string', enum: ['major', 'minor'], description: 'Rough size of the impact on the portfolio.' },
            intensity: { type: 'number', description: 'Continuous 0–1 emphasis score within the magnitude bucket, used to size the pill in the UI. Reserve values above 0.8 for the most consequential themes.' },
            rationale: { type: 'string', description: '1–2 sentences explaining why this theme has this effect on the client\'s specific holdings. Name affected companies by full name (never ticker). No hedging.' },
            linkedHeadlineIds: {
              type: 'array',
              description: 'IDs of headlines that feed this theme. Use the integer IDs provided in the context block.',
              items: { type: 'integer' },
            },
          },
          required: ['name', 'polarity', 'magnitude', 'intensity', 'rationale', 'linkedHeadlineIds'],
        },
      },
    },
    required: ['themes'],
  },
};

const ANALYZE_NEWS_TOOL = {
  name: 'analyze_news_impact',
  description: 'Return a concise summary and portfolio-impact analysis for a market news story.',
  input_schema: {
    type: 'object',
    properties: {
      summary: {
        type: 'string',
        description: 'A 2–3 sentence plain-text summary of the article. Under 500 characters. No preamble, no hedging.',
      },
      impact: {
        type: 'string',
        description: '2–4 plain-text sentences on the specific impact of this story on the client\'s current holdings. Name affected companies by full name (never ticker). If the story has no meaningful connection to the portfolio, say so briefly in one sentence. No preamble, no disclaimers.',
      },
    },
    required: ['summary', 'impact'],
  },
};

const CLASSIFY_SECTORS_TOOL = {
  name: 'classify_sectors',
  description: 'Classify each security into its top-level GICS sector and return a short analysis of the resulting sector mix.',
  input_schema: {
    type: 'object',
    properties: {
      long_classifications: {
        type: 'array',
        description: 'One entry per long-basket security.',
        items: {
          type: 'object',
          properties: {
            ticker: { type: 'string' },
            sector: { type: 'string', description: 'One of: Energy, Materials, Industrials, Consumer Discretionary, Consumer Staples, Health Care, Financials, Information Technology, Communication Services, Utilities, Real Estate.' },
          },
          required: ['ticker', 'sector'],
        },
      },
      short_classifications: {
        type: 'array',
        description: 'One entry per short-basket security.',
        items: {
          type: 'object',
          properties: {
            ticker: { type: 'string' },
            sector: { type: 'string' },
          },
          required: ['ticker', 'sector'],
        },
      },
      message: { type: 'string', description: '3–4 sentence analysis: (1) briefly acknowledge the date, (2) name the dominant sector tilts in each basket, (3) characterize the overall positioning as defensive vs aggressive.' },
    },
    required: ['long_classifications', 'short_classifications', 'message'],
  },
};

const GICS_SECTORS = [
  'Energy', 'Materials', 'Industrials', 'Consumer Discretionary', 'Consumer Staples',
  'Health Care', 'Financials', 'Information Technology', 'Communication Services',
  'Utilities', 'Real Estate',
];

const ASK_MARKET_TOOL = {
  name: 'ask_market',
  description: 'Delegate to a real-time market-research sub-agent (Gemini Pro with Google Search) to answer questions that require CURRENT, real-time market data, live prices, or today\'s news. Use this for questions like "what\'s driving my portfolio today?", "any news on Costco?", "why is tech down today?", "how are we doing today?", "what\'s happening in markets?". The sub-agent automatically receives the client\'s current portfolio composition along with the question. Do NOT use this for: backtest/historical questions (discuss directly), historical holdings lookup (use show_holdings), sector mix (use show_sectors), period performance (use show_performance), or general portfolio questions that don\'t require real-time data. The sub-agent\'s answer is shown directly to the client — you do not need to summarize or re-phrase it.',
  input_schema: {
    type: 'object',
    properties: {
      question: { type: 'string', description: 'The client\'s question, phrased clearly for a market-research sub-agent (you may rephrase to be more specific if helpful). The sub-agent already has the portfolio composition, so focus the question on what needs real-time market context.' },
    },
    required: ['question'],
  },
};

const HOME_ADVISOR_TOOL = {
  name: 'respond_to_client',
  description: 'Respond to the client on the Home dashboard. The `message` field supports GitHub-flavored markdown (tables, lists, bold, code) — use markdown tables for compact structured answers (e.g. comparing 3-5 holdings or sectors). Optionally attach an inline `chart` payload for visualizations like time series or bar comparisons. This is a portfolio-monitoring view — you can discuss the current portfolio, historical performance, holdings, and sector exposures, but you cannot change strategy parameters from this view (if the client wants adjustments, tell them to use the Strategy tab).',
  input_schema: {
    type: 'object',
    properties: {
      message: {
        type: 'string',
        description: 'Your conversational response, in GitHub-flavored markdown. Use plain prose (2–4 sentences) by default. Use markdown tables when comparing structured data (e.g. holdings × metrics). Use **bold** for emphasis, lists for itemizing. End with a question that invites the client\'s next action.',
      },
      chart: {
        type: 'object',
        description: 'OPTIONAL inline chart shown below the message. Use for visual comparisons, time series, distributions. Skip when prose or a markdown table conveys the answer better, or when a full modal (show_performance / show_holdings / show_sectors) is more appropriate. Best paired with data fetched via query_historical_data.',
        properties: {
          chartType: {
            type: 'string',
            enum: ['line', 'bar', 'area'],
            description: 'line / area = time series. bar = comparisons across categories.',
          },
          title:  { type: 'string', description: 'Optional short title above the chart.' },
          xLabel: { type: 'string', description: 'Optional X-axis label.' },
          yLabel: { type: 'string', description: 'Optional Y-axis label.' },
          series: {
            type: 'array',
            description: 'One or more data series sharing the same X-axis. For a single-series chart, pass one entry.',
            items: {
              type: 'object',
              properties: {
                name:  { type: 'string', description: 'Series label, e.g. "Strategy" or "S&P 500".' },
                color: { type: 'string', description: 'Optional hex color (e.g. "#6366f1"). Auto-assigned if omitted.' },
                data:  {
                  type: 'array',
                  description: 'Array of {x, y} points (string or number x, numeric y). Aim for ≤80 points per series — downsample longer ranges via query_historical_data\'s sampleEvery.',
                  items: {
                    type: 'object',
                    properties: {
                      x: { description: 'X value — string (date "YYYY-MM-DD", ticker name, etc.) or number.' },
                      y: { type: 'number' },
                    },
                    required: ['x', 'y'],
                  },
                },
              },
              required: ['name', 'data'],
            },
          },
        },
        required: ['chartType', 'series'],
      },
    },
    required: ['message'],
  },
};

const QUERY_HISTORICAL_DATA_TOOL = {
  name: 'query_historical_data',
  description: 'Fetch raw historical data for analysis. Call this BEFORE respond_to_client when the client\'s question needs precise numbers (e.g. "how did the strategy do in 2022?", "what\'s SPY done today?", "compare the strategy\'s growth across 2018-2020"). After you receive the data as a tool_result, make a follow-up respond_to_client call summarizing in prose and (optionally) attaching an inline chart with the chart field. Available datasets: "intraday" (today\'s 5-minute price bars for SPY and portfolio holdings), "backtest" (daily Strategy and S&P 500 values, ~16 years from 2010 to today). Designed extensibility: future datasets like fundamentals will be added under new dataset names.',
  input_schema: {
    type: 'object',
    properties: {
      dataset: {
        type: 'string',
        enum: ['intraday', 'backtest'],
        description: '"intraday" returns today\'s 5-min bars for the specified tickers. "backtest" returns daily Strategy + S&P 500 values, optionally filtered/downsampled.',
      },
      tickers: {
        type: 'array',
        items: { type: 'string' },
        description: 'For dataset=intraday only: tickers to return. Use "SPY" for the benchmark, or any portfolio holding\'s ticker symbol. If omitted, returns all available tickers.',
      },
      fromDate: { type: 'string', description: 'For dataset=backtest only: start date YYYY-MM-DD (inclusive). If omitted, starts at the earliest available (~2010-01-04).' },
      toDate:   { type: 'string', description: 'For dataset=backtest only: end date YYYY-MM-DD (inclusive). If omitted, ends at the latest available.' },
      sampleEvery: {
        type: 'integer',
        description: 'For dataset=backtest only: optional. Return every Nth row to keep the response compact. Use 1 for full daily resolution (short ranges < 1 yr), 5 for ~weekly (1-3 yr ranges), 21 for ~monthly (3+ yr ranges). Aim for under 500 rows in any single response.',
      },
    },
    required: ['dataset'],
  },
};

function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// "2026-06-05" → "June 5, 2026". TTS-friendly month-day-year form per the
// LANGUAGE STYLE date rule. Returns the input unchanged if it can't parse.
function isoToReadableDate(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[+m[2] - 1]} ${+m[3]}, ${m[1]}`;
}

// Runtime block telling the advisor what holdings snapshot range is
// actually available right now. Re-injected each turn so it tracks new
// CSVs added to public/ without needing a prompt redeploy. The model is
// instructed to TRUST this block over any range hardcoded elsewhere.
function buildHoldingsAvailability(dates) {
  if (!Array.isArray(dates) || dates.length === 0) return '';
  const today    = todayIsoLocal();
  const earliest = dates[0];
  const latest   = dates[dates.length - 1];
  return `\n\nHOLDINGS AVAILABILITY (refreshed each turn — this is the source of truth):\n` +
    `Today's date is ${isoToReadableDate(today)} (ISO: ${today}).\n` +
    `Weekly portfolio snapshots are available from ${isoToReadableDate(earliest)} through ${isoToReadableDate(latest)}. The most recent snapshot is the CURRENT portfolio — it stays in force until the next weekly rebalance.\n` +
    `\nWhen the client asks about CURRENT / PRESENT / TODAY / NOW holdings or sector mix:\n` +
    `  • YOUR FIRST AND ONLY ACTION THIS TURN is to call show_holdings or show_sectors with date=${today} and isCurrent=true. Do NOT also call respond_to_client this turn — the NO-PREAMBLE TOOL-CALL RULE forbids any "let me pull that up", "let me show you", "pulling up your holdings now", or similar acknowledgment message before the tool. The modal opening IS the acknowledgment. The system snaps internally to the latest available snapshot (${latest}); you do NOT need to pass the snapshot date yourself.\n` +
    `  • The post-tool commentary (generated NEXT turn, AFTER the modal data is piped back to you in a tool_result, NOT this turn) should open with "As of today, ${isoToReadableDate(today)}, …" — and must NEVER cite the snapshot date (${isoToReadableDate(latest)}) as an upper bound or as "the most recent snapshot available". Treat the latest snapshot AS current; do not surface the rebalance cadence to the client unless they ask for it specifically.\n`;
}

async function callGemini(systemPrompt, userMessage) {
  const res = await apiFetch('/api/gemini', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      contents: [{ role: 'user', parts: [{ text: userMessage }] }],
      systemInstruction: { parts: [{ text: systemPrompt }] },
      tools: [{ google_search: {} }],
    }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Gemini ${res.status}: ${err}`);
  }
  const data = await res.json();
  const parts = data?.candidates?.[0]?.content?.parts || [];
  const text = parts.map(p => p.text).filter(Boolean).join('\n').trim();
  if (!text) throw new Error('Gemini returned no text');
  return text;
}

// Stable signature for (params + holdings). Used to cache the style/positioning
// brief — it only regenerates when the deployed portfolio actually changes.
function portfolioSignature(homeData, params) {
  if (!homeData || !params) return '';
  const ls = (homeData.longs  || []).map(h => `${h.ticker}:${h.weight.toFixed(4)}`).join(',');
  const ss = (homeData.shorts || []).map(h => `${h.ticker}:${h.weight.toFixed(4)}`).join(',');
  return `${params.grossExposure}|${params.shortPercent}|${params.goldPercent}|${params.bondsPercent}|L:${ls}|S:${ss}`;
}

// Stable signature for today's drivers. Regenerates the "today" brief whenever
// the contribution distribution shifts (i.e. the CSV has been re-pulled).
function driversSignature(drivers) {
  if (!drivers || drivers.length === 0) return 'empty';
  const total = drivers.reduce((a, d) => a + (d.contribution || 0), 0).toFixed(3);
  const parts = drivers.map(d => `${d.ticker}:${d.contribution.toFixed(3)}`).join(',');
  return `${total}|${parts}`;
}

// Shared instruction injected into every prompt that produces user-facing
// text. The buildPortfolioBrief output uses precise industry terms (LLMs
// need them for accuracy), but downstream copy must translate to retail
// vocabulary that matches the Strategy/Home UI.
const RETAIL_LANGUAGE_GUIDE = `LANGUAGE STYLE — important:
The reader is a self-directed retail investor unfamiliar with portfolio-management jargon. When writing:
  • AVOID these terms: "gross exposure", "leverage", "sleeve", "drawdown", "Sharpe ratio", "volatility", "alpha", "beta", "long/short equity strategy".
  • USE plain-English equivalents:
      - "total invested" or "investment level"     instead of "gross exposure"
      - "drop hedge" or "insurance against drops"  instead of "short positions" or "short equity sleeve"
      - "stocks expected to rise" or "long stocks" instead of "long equity"
      - "biggest dip" or "worst stretch"           instead of "drawdown"
      - "smoother / bumpier than the S&P 500"      instead of "lower / higher volatility"
  • Frame outcomes in dollars where the data supports it, not just percentages.
  • Refer to companies by full name (never ticker symbols).
  • Tone: warm, confident, plain-spoken. Assume the reader is smart but new to the vocabulary.
  • Dates: write calendar dates as "Month D, YYYY" — e.g. "June 1, 2026" or "January 15, 2026". NEVER use ISO format like "2026-06-01" or "2026/06/01" — these are unreadable by the voice synthesizer and the client will hear them spelled out character by character. The same rule applies to dates inside markdown tables and chart labels.`;

function buildPortfolioBrief(homeData, params) {
  if (!homeData) return '[Portfolio snapshot not yet loaded.]';
  const { longs, shorts, longPct, shortPct, netSectors } = homeData;
  const fmt = (r, s, sign) => `  - ${r.name} (${r.ticker}): ${sign === -1 ? '-' : ''}${(r.weight * s).toFixed(2)}% of portfolio`;
  return [
    `STRATEGY PARAMETERS:`,
    `  • Gross exposure: ${params.grossExposure}%`,
    `  • Long equity sleeve: ${longPct}% of portfolio`,
    `  • Short equity sleeve: ${shortPct}%`,
    `  • Gold sleeve: ${params.goldPercent}%`,
    `  • Fixed income sleeve: ${params.bondsPercent}%`,
    ``,
    `LONG POSITIONS (most recent snapshot, ${longs.length} names, ${longPct}% of portfolio):`,
    ...longs.map(r => fmt(r, longPct, 1)),
    ``,
    `SHORT POSITIONS (${shorts.length} names, ${shortPct}% of portfolio):`,
    ...shorts.map(r => fmt(r, shortPct, -1)),
    ``,
    `NET SECTOR EXPOSURE (long minus short, in portfolio %):`,
    ...netSectors.map(s => `  • ${s.sector}: ${s.net >= 0 ? '+' : ''}${s.net.toFixed(2)}%`),
  ].join('\n');
}

// ── Market-research sub-agent ────────────────────────────────────────────
// Two interchangeable backends. The chosen one is dispatched by
// askMarketAgent() based on MARKET_AGENT_PROVIDER (env-driven, see top of
// file). Both return plain prose answers — same caller contract.

async function askMarketAgentGemini(question, portfolioBrief) {
  const system = `You are a real-time market research sub-agent for a wealth management platform called Puro Wealth. You have access to Google Search for current market data, live quotes, and today's news. Today's date is ${todayIsoLocal()}.

A client is viewing their live portfolio and has a question. Here is their current portfolio composition:

${portfolioBrief}

${RETAIL_LANGUAGE_GUIDE}

Use Google Search to ground your answer in today's market data when the question involves live prices, recent news, today's market action, or company-specific developments. Be specific — cite actual numbers, percentage moves, and news items. Keep the response to 4–6 sentences. Do not hedge or disclaim — give a direct, informed answer based on what you can find.`;
  return await callGemini(system, question);
}

async function askMarketAgentClaude(question, portfolioBrief) {
  const system = `You are a real-time market research sub-agent for a wealth management platform called Puro Wealth. You have access to a web_search tool for current market data, live quotes, and today's news. Today's date is ${todayIsoLocal()}.

A client is viewing their live portfolio and has a question. Here is their current portfolio composition:

${portfolioBrief}

${RETAIL_LANGUAGE_GUIDE}

Use the web_search tool to ground your answer in today's market data when the question involves live prices, recent news, today's market action, or company-specific developments. You may issue multiple searches if the question spans several topics or holdings — search each one specifically. Be concrete — cite actual numbers, percentage moves, and news items when you find them. Keep the response to 4–6 sentences. Do not hedge or disclaim — give a direct, informed answer based on what you found.`;

  // Server-side web_search tool — Anthropic runs the search/synthesis loop
  // on its end and returns the final text in one round-trip.
  const body = {
    model: CLAUDE_MODEL,
    max_tokens: 16384,
    system,
    messages: [{ role: 'user', content: question }],
    tools: [{ type: 'web_search_20250305', name: 'web_search', max_uses: 16 }],
  };
  const res = await apiFetch('/api/claude', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Claude (web search) ${res.status}: ${err}`);
  }
  const data = await res.json();
  // Response can include text + web_search_tool_use + web_search_tool_result
  // blocks; surface only the text. Citations are attached as `citations`
  // arrays per text block — ignored for now (chat is plain prose).
  const textBlocks = (data.content || []).filter(c => c.type === 'text');
  const answer = textBlocks.map(t => t.text).join('\n\n').trim();
  if (!answer) throw new Error('Claude (web search) returned no text response.');
  return answer;
}

// Router. Picks the backend based on MARKET_AGENT_PROVIDER. Anything other
// than 'claude' falls through to Gemini, so a typo or empty value defaults to
// the original behavior rather than failing loudly.
async function askMarketAgent(question, portfolioBrief) {
  if (MARKET_AGENT_PROVIDER === 'claude') {
    return await askMarketAgentClaude(question, portfolioBrief);
  }
  return await askMarketAgentGemini(question, portfolioBrief);
}

async function classifyHoldings(longs, shorts, displayDate) {
  const fmtRow = r => `  - ${r.name} (${r.ticker})`;
  const system =
    `You are classifying portfolio holdings into top-level GICS sectors. The sectors are exactly: ${GICS_SECTORS.join(', ')}.\n\n` +
    `Portfolio as of: ${displayDate}\n\n` +
    `Long basket:\n${longs.map(fmtRow).join('\n')}\n\n` +
    `Short basket:\n${shorts.map(fmtRow).join('\n')}\n\n` +
    `Call classify_sectors with one entry per security (ticker + sector). Include a 3–4 sentence analysis message that:\n` +
    `  • references the portfolio as of ${displayDate}\n` +
    `  • refers to companies by their full name only (e.g. "McDonald's"), NEVER by ticker symbol (never "MCD")\n` +
    `  • comments on dominant sector tilts in each basket\n` +
    `  • characterizes the overall positioning as defensive vs aggressive`;
  // 4096-token budget — typical portfolio of ~22 holdings serialises to
  // 1.5–2k tokens for the long+short classifications plus the analysis
  // message; default 1024 was too tight and caused silent truncation that
  // returned malformed (non-array) fields.
  const { input } = await callClaude(
    system,
    [{ role: 'user', content: `Classify the holdings for ${displayDate}.` }],
    [CLASSIFY_SECTORS_TOOL],
    undefined,
    4096,
  );
  return input;
}

function aggregateSectors(classifications, holdings, sleevePct) {
  // Defensive: Claude tool-use occasionally returns a non-array shape for
  // optional fields (or omits them when output is truncated). Coerce so a
  // bad classification just yields "Other" buckets rather than a TypeError.
  const safe = Array.isArray(classifications)
    ? classifications.filter(c => c && typeof c.ticker === 'string' && typeof c.sector === 'string')
    : [];
  const byTicker = new Map(safe.map(c => [c.ticker, c.sector]));
  const totals = new Map();
  for (const h of holdings) {
    const sector = byTicker.get(h.ticker) || 'Other';
    const pct = h.weight * sleevePct;
    totals.set(sector, (totals.get(sector) || 0) + pct);
  }
  return [...totals.entries()]
    .map(([sector, portfolioPct]) => ({ sector, portfolioPct }))
    .sort((a, b) => b.portfolioPct - a.portfolioPct);
}

function buildStateContext(params, stats) {
  const longPct = 100 - params.shortPercent - params.goldPercent - params.bondsPercent;
  const statsBlock = stats
    ? `\nBacktest performance (Jan 2010 – present):\n  Annualised return: ${stats.annualizedReturn.toFixed(1)}%\n  Sharpe ratio: ${stats.sharpe.toFixed(2)}\n  Max drawdown: ${stats.maxDrawdown.toFixed(1)}%\n  Ann. std deviation: ${stats.stdDev.toFixed(1)}%\n  Best month: ${stats.bestMonth.toFixed(1)}%\n  Worst month: ${stats.worstMonth.toFixed(1)}%`
    : '';
  return `\n\nCURRENT STRATEGY STATE:\nA strategy is already built and the client has seen its backtest. Current parameters:\n  grossExposure: ${params.grossExposure}%\n  longPercent (derived): ${longPct}%\n  shortPercent: ${params.shortPercent}%\n  goldPercent: ${params.goldPercent}%\n  bondsPercent: ${params.bondsPercent}%\n${statsBlock}\n\nThe client may now ask questions about the strategy, request adjustments, or confirm they're ready to deploy. When they request an adjustment (e.g. "more gold", "reduce leverage", "set bonds to 20%", "make it safer"), include all four updated parameter values in your tool call. When they just chat, ask a question, or acknowledge, respond naturally without including parameter fields.`;
}

// Detect a clear "I want to deploy" message. Used as a hard tool-filter
// gate so the model cannot accidentally regress into show_sectors /
// show_holdings / show_performance when the previous turn primed one of
// them — even if the in-prompt DEPLOYMENT-INTENT PRIORITY rule is
// momentarily under-weighted by the LLM.
//
// The gate must stay NARROW. A false positive strips the show_* tools for
// the turn, and if the client was really asking to SEE something the model
// has no valid move left. Observed 2026-09-30: "walk me through the
// positions I'd own today if I were to deploy this" tripped a bare
// `deploy\s+` match, show_holdings was removed, and the model (barred from
// preamble, told deploy intent means "ask for the allocation") repeated its
// previous message verbatim. So: (1) the affirmative patterns carry word
// boundaries, (2) conditional / hypothetical mentions of deploying don't
// count, and (3) an explicit request to see portfolio information wins —
// the model can take the deploy step on the next turn.
const DEPLOY_INTENT_RE = new RegExp(
  [
    "\\bready\\s+(?:to\\s+)?(?:deploy|go\\s+live|invest)\\b",
    "\\bdeploy\\s+(?:it|this|now|the\\s+strategy)\\b",
    "\\b(?:please\\s+)?deploy\\s*[.!]*\\s*$",
    "\\blet'?s\\s+(?:go\\s+live|deploy|do\\s+(?:this|it)|proceed|move\\s+forward)\\b",
    "\\bgo\\s+(?:ahead|live)\\b",
    "\\bi'?m\\s+(?:ready|done|sold|in|good\\s+to\\s+go)\\b",
    "\\bi'?ll\\s+take\\s+(?:it|that|this)\\b",
    "\\bsounds?\\s+good[,.]?\\s+(?:let'?s|i'?ll|deploy)\\b",
    "\\bapprove\\b",
    "\\bsign(?:ing)?\\s+off\\b",
    "\\blooks?\\s+good[,.]?\\s+(?:let'?s|deploy)\\b",
  ].join("|"),
  "i"
);
// Talking ABOUT deploying rather than doing it: "if I were to deploy this",
// "before I deploy", "once I go live", "what happens when I invest".
const DEPLOY_HYPOTHETICAL_RE = /\b(?:if|before|when|once|after|whether|until|were\s+to|what\s+happens|what\s+would)\b[^.!?]{0,60}\b(?:deploy|go(?:ing)?\s+live|invest)/i;
// An explicit ask to see portfolio information this turn.
const PORTFOLIO_NOUN = "(?:holdings?|positions?|stocks?|companies|sectors?|industr(?:y|ies)|performance|portfolio|backtest)";
const PORTFOLIO_INFO_REQUEST_RE = new RegExp(
  [
    "\\b(?:show|walk|take)\\s+me\\s+(?:through|what|the)\\b",
    "\\bwhat'?s\\s+in\\b",
    "\\bwhat\\s+is\\s+in\\b",
    "\\bwhat\\s+(?:do|would|will|did)\\s+(?:i|we|it)\\s+(?:own|hold)\\b",
    "\\b(?:see|view|look\\s+at|check(?:\\s+out)?|list|pull\\s+up|break\\s*down)\\b[^.!?]{0,30}\\b" + PORTFOLIO_NOUN,
    "\\b" + PORTFOLIO_NOUN + "\\b[^.!]{0,80}\\?",
    "\\b(?:what|which|how)\\b[^.!?]{0,60}\\b" + PORTFOLIO_NOUN + "\\b[^.!]*\\?",
  ].join("|"),
  "i"
);
function detectDeployIntent(text) {
  const t = text || '';
  if (!DEPLOY_INTENT_RE.test(t)) return false;
  if (DEPLOY_HYPOTHETICAL_RE.test(t)) return false;
  if (PORTFOLIO_INFO_REQUEST_RE.test(t)) return false;
  return true;
}

// opts.ignoreDeployGate — offer the full toolset even if the deploy gate
//   would fire (used by sendMessage's verbatim-repeat retry).
// opts.systemAddendum  — extra instruction appended last to the system prompt.
async function consultAdvisor(conversationHistory, cfg, currentParams, currentStats, currentMemory, opts = {}) {
  const { ignoreDeployGate = false, systemAddendum = '' } = opts;
  const start = conversationHistory.findIndex(m => m.role === 'user');
  if (start === -1) throw new Error('no user messages');
  const msgs = conversationHistory.slice(start).map(m => ({
    role: m.role === 'ai' ? 'assistant' : 'user',
    content: m.text,
  }));
  const memCtx = buildMemoryContext(currentMemory);
  // Snapshot availability is runtime-derived (auto-tracks new CSVs added
  // to public/), then appended last so it carries the most authoritative
  // signal about which dates the model can claim.
  const dates = await getDates().catch(() => []);
  const holdingsCtx = buildHoldingsAvailability(dates);
  const systemPrompt = (currentParams
    ? cfg.systemPrompt + buildStateContext(currentParams, currentStats)
    : cfg.systemPrompt) + memCtx + holdingsCtx + systemAddendum;

  // When the latest client message expresses clear deploy intent, drop the
  // data-display tools from the toolset. Backstop for the in-prompt
  // DEPLOYMENT-INTENT PRIORITY rule, which the model has been observed to
  // skip past when a previous turn just called one of the show_* tools.
  const lastUserText = [...conversationHistory].reverse().find(m => m.role === 'user')?.text || '';
  const deployIntent = !ignoreDeployGate && currentParams && detectDeployIntent(lastUserText);
  if (deployIntent) console.log('[advisor] deploy-intent gate ON — show_* tools withheld this turn');
  const tools = !currentParams
    ? [ADVISOR_TOOL]
    : deployIntent
      ? [ADVISOR_TOOL, DEPLOY_STRATEGY_TOOL]
      : [ADVISOR_TOOL, SHOW_HOLDINGS_TOOL, SHOW_SECTORS_TOOL, SHOW_PERFORMANCE_TOOL, DEPLOY_STRATEGY_TOOL];
  return await callClaude(systemPrompt, msgs, tools);
}

// ── Holdings loader ──────────────────────────────────────────────────────────

let _datesManifest = null;
async function getDates() {
  if (_datesManifest) return _datesManifest;
  const res = await fetch('/holdings-dates.json');
  _datesManifest = await res.json();
  return _datesManifest;
}

function snapDate(targetIso, dates) {
  const target = new Date(targetIso).getTime();
  let best = dates[0], bestDiff = Infinity;
  for (const d of dates) {
    const diff = Math.abs(new Date(d).getTime() - target);
    if (diff < bestDiff) { bestDiff = diff; best = d; }
  }
  return best;
}

async function loadHoldingsCsv(date, side) {
  const fname = date.replace(/-/g, '_') + '_' + side + '.csv';
  const res = await fetch('/' + fname);
  if (!res.ok) throw new Error(`holdings file missing: ${fname}`);
  const text = await res.text();
  const { data } = Papa.parse(text, { header: true, skipEmptyLines: true });
  return data
    .map(row => ({
      ticker: row.Ticker?.trim() || '',
      name:   row.Name?.trim()   || '',
      weight: parseFloat(row.Weight) || 0,
    }))
    .filter(r => r.ticker && r.weight > 0)
    .sort((a, b) => b.weight - a.weight);
}

async function loadHoldings(requestedDate) {
  const dates = await getDates();
  const date = snapDate(requestedDate, dates);
  const [longs, shorts] = await Promise.all([
    loadHoldingsCsv(date, 'long'),
    loadHoldingsCsv(date, 'short'),
  ]);
  return { date, longs, shorts };
}

// ── Backtest + stats ──────────────────────────────────────────────────────────

function calcBacktest(sleeveData, params) {
  const gross = params.grossExposure / 100;
  const sp    = params.shortPercent  / 100;
  const gp    = params.goldPercent   / 100;
  const bp    = params.bondsPercent  / 100;
  const lp    = 1 - sp - gp - bp;

  let stratVal = 10000, benchVal = 10000;
  return sleeveData.map(row => {
    stratVal *= 1 + gross * (lp*row.long_sleeve + sp*row.short_sleeve + gp*row.gold_sleeve + bp*row.bonds_sleeve);
    benchVal *= 1 + row.sp500;
    return { date: row.date, Strategy: Math.round(stratVal), 'S&P 500': Math.round(benchVal) };
  });
}

function calcStats(data) {
  const returns = data.slice(1).map((d, i) => (d.Strategy - data[i].Strategy) / data[i].Strategy);
  const n = returns.length;
  if (n === 0) return null;

  const annualizedReturn = (Math.pow(data[n].Strategy / data[0].Strategy, 12 / n) - 1) * 100;
  const worstMonth       = Math.min(...returns) * 100;
  const bestMonth        = Math.max(...returns) * 100;

  let peak = -Infinity, maxDD = 0;
  for (const d of data) {
    if (d.Strategy > peak) peak = d.Strategy;
    const dd = (peak - d.Strategy) / peak;
    if (dd > maxDD) maxDD = dd;
  }

  const mean     = returns.reduce((a, b) => a + b, 0) / n;
  const variance = returns.reduce((a, r) => a + (r - mean) ** 2, 0) / n;
  const stdDev   = Math.sqrt(variance * 12) * 100;
  const sharpe   = (annualizedReturn - 2) / stdDev;

  return { annualizedReturn, worstMonth, bestMonth, maxDrawdown: maxDD * 100, stdDev, sharpe };
}

// ── Context ───────────────────────────────────────────────────────────────────

const AdvisorContext = createContext(null);
export const useAdvisor = () => useContext(AdvisorContext);

export function AdvisorProvider({ children }) {
  const [config, setConfig]             = useState(null);
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState('');
  const [phase, setPhase]               = useState('chat');
  const [params, setParams]             = useState(null);
  // Active risk profile + tilts. Power-user / raw-number paths set
  // activeProfile = null ("Custom" mode); the profile dial reflects this.
  const [activeProfile, setActiveProfile] = useState(null);
  const [profileTilts,  setProfileTilts]  = useState({ ...ZERO_TILTS });
  const [backtestData, setBacktestData] = useState(null);
  const [stats, setStats]               = useState(null);
  const [voiceActivePage, setVoiceActivePage] = useState(null); // 'onboarding' | 'strategy' | 'home' | null
  const [voiceState, setVoiceState]           = useState('idle'); // 'idle' | 'listening' | 'speaking' (strategy/home only)
  const [voiceTranscript, setVoiceTranscript] = useState([]);    // collected during onboarding
  // HeyGen LiveAvatar (onboarding).
  // sessionToken is the per-session JWT minted by /v1/sessions/token; an
  // empty string means "not connected". Setting it to a real token mounts
  // the LiveAvatar SDK provider in Advisor.js; clearing it tears down.
  const [liveAvatarToken,    setLiveAvatarToken]    = useState('');
  const [liveAvatarLoading,  setLiveAvatarLoading]  = useState(false);
  const [liveAvatarError,    setLiveAvatarError]    = useState('');
  // HeyGen LiveAvatar (Strategy / Home chat — hybrid mode). Token is minted
  // when voice starts on those pages; the ChatLiveAvatar component in
  // ChatPanel mounts the SDK against this token, registers itself via
  // registerChatAvatar(), and stays connected for the duration of the voice
  // session. Avatar speech is driven by Claude responses through
  // session.repeat() — see speakText().
  const [chatLiveAvatarToken, setChatLiveAvatarToken] = useState('');
  // Mirror chatLiveAvatarToken into a ref so mintChatAvatarToken() (called
  // from closures created on earlier renders) can read the latest value
  // without going stale. Also tracks an in-flight mint so a pre-mint in
  // finalizeOnboardingTranscript and the redundant mint in
  // startStrategyVoice don't fire two HeyGen sessions back-to-back.
  const chatLiveAvatarTokenRef = useRef('');
  const chatTokenMintingRef    = useRef(false);
  useEffect(() => { chatLiveAvatarTokenRef.current = chatLiveAvatarToken; }, [chatLiveAvatarToken]);
  // Onboarding-advisor carousel state. selectedAdvisorIdx is the user's pick;
  // advisorPreviews maps avatarId → fetched preview-image URL.
  const [selectedAdvisorIdx, setSelectedAdvisorIdx] = useState(0);
  const [advisorPreviews,    setAdvisorPreviews]    = useState({});
  // Per-advisor voice id, keyed by advisor.id. Resolved lazily on mount from
  // each Convai agent's config so Strategy/Home TTS uses the same voice the
  // user heard during onboarding. Falls back to EL_VOICE_ID until loaded.
  const [advisorVoiceIds, setAdvisorVoiceIds] = useState({});
  const [isBootstrapping, setIsBootstrapping] = useState(false); // true while bootstrapFromTranscript runs
  const [bootstrapError,  setBootstrapError]  = useState('');
  const [holdingsModal, setHoldingsModal] = useState(null);
  const [deployed, setDeployed]         = useState(false);
  const [allocationUsd, setAllocationUsd] = useState(null); // dollars allocated at deploy time
  // Persistent memory loaded from localStorage on mount. null = cold-start
  // user; non-null = returning user (drives "welcome back" branches).
  const [memory, setMemory] = useState(() => loadMemory());
  const [memoryViewOpen, setMemoryViewOpen]   = useState(false);
  const [metricsViewOpen, setMetricsViewOpen] = useState(false);
  const [homeMessages, setHomeMessages] = useState([]);
  const [homeInput, setHomeInput]       = useState('');
  const [homePhase, setHomePhase]       = useState('chat');
  const [homeData, setHomeData]         = useState(null);
  const [descCache, setDescCache]       = useState({});
  const [strategyEngaged, setStrategyEngaged] = useState(false); // true once user sends on Strategy
  const [homeEngaged,     setHomeEngaged]     = useState(false); // true once user sends on Home
  // Portfolio briefing state — lives in context so it survives tab switches.
  // key = portfolioSignature / driversSignature at the time the text was generated.
  const [styleBrief, setStyleBrief] = useState({ text: '', key: '', loading: false, error: '' });
  const [todayBrief, setTodayBrief] = useState({ text: '', key: '', loading: false, error: '' });
  // "Since we spoke" briefing — only shown for returning users on Home.
  const [sinceWeSpokeBrief, setSinceWeSpokeBrief] = useState({ text: '', key: '', loading: false, error: '' });
  // Markets & Context tab: Finnhub headlines + per-headline Claude analysis.
  const [marketsHeadlines, setMarketsHeadlines] = useState({ items: [], loading: false, error: '' });
  const [marketsAnalysis,  setMarketsAnalysis]  = useState({});         // { [headlineId]: { summary, impact, loading, error } }
  const [selectedHeadlineId, setSelectedHeadlineId] = useState(null);
  const [marketsThemes, setMarketsThemes] = useState({ items: [], key: '', loading: false, error: '' });
  const [marketsView,   setMarketsView]   = useState('today');          // 'today' | 'drivers'

  const bottomRef      = useRef(null);
  const inputRef       = useRef(null);
  // voiceActivePageRef mirrors voiceActivePage so async callbacks see the
  // correct page at fire time.
  const voiceActivePageRef = useRef(null);
  const transcriptRef      = useRef([]);
  const sleeveDataRef  = useRef(null);
  const configRef      = useRef(null);
  const homeBottomRef  = useRef(null);
  const homeInputRef   = useRef(null);
  // ElevenLabs STT (Scribe) + ElevenLabs TTS refs (Strategy/Home only).
  // recorderRef holds the active MediaRecorder; micStreamRef the underlying
  // MediaStream; audioCtxRef the Web Audio context for VAD; vadRafRef the
  // animation-frame id for the VAD tick loop. Each call to startListeningEL
  // tears down the prior set before starting fresh.
  const recorderRef      = useRef(null);
  const micStreamRef     = useRef(null);
  const audioCtxRef      = useRef(null);
  const vadRafRef        = useRef(0);
  const sttRunIdRef      = useRef(0);  // tag each utterance for stale-result guarding
  // Tracks consecutive empty-transcript / soft-error restarts so we cap retries.
  // Reset on successful capture or when stopVoice is called.
  const noSpeechRestartsRef = useRef(0);
  const audioRef       = useRef(null);
  const lastSpokenRef  = useRef({ strategy: -1, home: -1 });
  // Mirrors of conversation/phase/params/stats so the voice submit() path
  // can read fresh values without resorting to nested functional updaters
  // (which are fragile under React 18's render scheduling).
  const messagesRef    = useRef([]);
  const phaseRef       = useRef('chat');
  const paramsRef      = useRef(null);
  const statsRef       = useRef(null);
  const homeMessagesRef = useRef([]);
  const homePhaseRef    = useRef('chat');
  // Active LiveAvatarSession instance for the chat-mode (Strategy/Home) hybrid.
  // Set by ChatLiveAvatar via registerChatAvatar(); read by speakText() to
  // route Claude responses through session.repeat() instead of ElevenLabs.
  // Ready becomes true after SESSION_STREAM_READY fires.
  const chatAvatarSessionRef = useRef(null);
  const chatAvatarReadyRef   = useRef(false);
  // Holds the onEnd callback (re-open mic) until AVATAR_SPEAK_ENDED fires.
  // Mirrors the audio.onended path in the ElevenLabs branch.
  const pendingAvatarSpeakEndRef = useRef(null);
  // Holds a resolve-fn set right before session.repeat() so we can tell
  // whether HeyGen accepted the command. AVATAR_SPEAK_STARTED calls it; if
  // it doesn't fire within ~5s we assume HeyGen silently dropped the repeat
  // (a real intermittent — same client-side code produces lip-sync one run
  // and silent-fidget the next) and fall through to ElevenLabs so the user
  // still hears the message.
  const pendingAvatarSpeakStartedRef = useRef(null);
  const descCacheRef   = useRef({});
  // Mirrors styleBrief / todayBrief for reliable dedup inside async closures.
  const styleBriefRef  = useRef({ text: '', key: '', loading: false });
  const todayBriefRef  = useRef({ text: '', key: '', loading: false });
  const sinceWeSpokeBriefRef = useRef({ text: '', key: '', loading: false });
  const marketsHeadlinesRef = useRef({ items: [], loading: false, error: '' });
  const marketsAnalysisRef  = useRef({});
  const marketsThemesRef    = useRef({ items: [], key: '', loading: false, error: '' });
  // Holds the in-flight Finnhub fetch promise so concurrent callers
  // (MarketsToday and PortfolioDrivers mounting back-to-back) share one fetch.
  const headlinesPromiseRef = useRef(null);
  // Mirrors advisorVoiceIds + selectedAdvisorIdx for speakText, which is
  // sometimes called from long-lived closures where state would be stale.
  const advisorVoiceIdsRef = useRef({});
  // Mirror persistent memory in a ref for reads inside long-lived closures
  // (chat handlers, onbeforeunload, async write paths) without re-binding.
  const memoryRef = useRef(memory);
  const selectedAdvisorIdxRef = useRef(0);

  const navigate = useNavigate();


  useEffect(() => {
    Promise.all([loadSleeveData(), loadConfig()]).then(([data, cfg]) => {
      sleeveDataRef.current = data;
      setConfig(cfg);
      configRef.current = cfg;
      setMessages([{ role: 'ai', text: cfg.opening }]);
    });
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, phase]);

  // ── Profile / tilt setters ────────────────────────────────────────────────

  // Apply a fresh params bundle and recompute backtest + stats. Used by all
  // three control surfaces (profile click, tilt slider, raw-number entry).
  function applyParams(p, { profileKey, tilts } = {}) {
    setParams(p);
    setActiveProfile(profileKey ?? null);
    setProfileTilts(tilts ? { ...tilts } : { ...ZERO_TILTS });
    const bt = sleeveDataRef.current ? calcBacktest(sleeveDataRef.current, p) : [];
    const st = bt.length ? calcStats(bt) : null;
    setBacktestData(bt);
    setStats(st);
    setPhase('results');
  }

  // User clicks a profile button: snap to that preset, reset tilts.
  function selectProfile(profileKey) {
    if (!RISK_PROFILES[profileKey]) return;
    const p = paramsFromProfileAndTilts(profileKey, ZERO_TILTS);
    applyParams(p, { profileKey, tilts: ZERO_TILTS });
  }

  // User drags one tilt slider (or the agent applies a tilt). value is the
  // absolute tilt in pp from the active profile's baseline.
  function setTilt(key, value) {
    if (!activeProfile) return;
    const newTilts = { ...profileTilts, [key]: Math.round(value) };
    const p = paramsFromProfileAndTilts(activeProfile, newTilts);
    applyParams(p, { profileKey: activeProfile, tilts: newTilts });
  }

  // Power-user escape: set raw numbers directly. Flips the dial to "Custom".
  function setRawParams(p) {
    applyParams(p, { profileKey: null, tilts: ZERO_TILTS });
  }

  // ── Core send ─────────────────────────────────────────────────────────────

  async function sendMessage(text, curMessages, curPhase, curParams, curStats, curConfig) {
    if (!text || curPhase === 'thinking' || !curConfig) return '';

    setStrategyEngaged(true);
    const next = [...curMessages, { role: 'user', text }];
    setMessages(next);
    setInput('');
    setPhase('thinking');

    const fallbackPhase = curParams !== null ? 'results' : 'chat';
    let aiReply = '';
    const pushAi = (t) => { aiReply = t; setMessages(m => [...m, { role: 'ai', text: t }]); };

    try {
      let { toolName, input } = await consultAdvisor(next, curConfig, curParams, curStats, memoryRef.current);

      // Verbatim-repeat guard. Re-sending the previous reply word for word is
      // never a valid answer (and in voice mode the client hears it twice).
      // It's the model's escape hatch when a turn leaves it no good move —
      // e.g. the deploy gate withholding show_holdings from a holdings
      // question. Retry once with the full toolset and an explicit nudge;
      // if it still repeats, accept it rather than loop.
      const prevAiText = [...curMessages].reverse().find(m => m.role === 'ai')?.text;
      const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (toolName === 'respond_to_client' && prevAiText && norm(input?.message) === norm(prevAiText)) {
        console.warn('[advisor] reply repeated the previous message verbatim — retrying once with full toolset');
        ({ toolName, input } = await consultAdvisor(next, curConfig, curParams, curStats, memoryRef.current, {
          ignoreDeployGate: true,
          systemAddendum:
            '\n\nRESPOND TO THE LATEST MESSAGE: Address the client\'s most recent message directly. ' +
            'Do not restate or repeat any earlier message of yours. If they are asking to see holdings, ' +
            'sectors or performance, call the matching show_* tool now.',
        }));
      }

      if (toolName === 'deploy_strategy') {
        if (typeof input.allocationUsd === 'number' && input.allocationUsd > 0) {
          setAllocationUsd(input.allocationUsd);
        }
        pushAi(input.message);
        setPhase('deployed');
        setDeployed(true);
        // Persist deployment to memory so the next session can reference it.
        updateMemoryStrategy({
          profile: activeProfile,
          tilts: profileTilts,
          allocationUsd: typeof input.allocationUsd === 'number' ? input.allocationUsd : undefined,
          selectedAdvisor: ADVISORS[selectedAdvisorIdxRef.current]?.id,
        });
        inputRef.current?.focus();
        return aiReply;
      }

      if (toolName === 'show_performance') {
        try {
          const { fromDate, toDate } = input;
          const from = new Date(fromDate), to = new Date(toDate);
          const sliced = (sleeveDataRef.current || []).filter(row => {
            const d = new Date(row.date);
            return d >= from && d <= to;
          });
          if (sliced.length < 2) {
            pushAi(`⚠️ I don't have enough data between ${fromDate} and ${toDate}. Try a wider range.`);
          } else {
            const periodData  = calcBacktest(sliced, curParams);
            const periodStats = calcStats(periodData);
            setHoldingsModal({ view: 'performance', fromDate, toDate, periodData, periodStats });

            try {
              const last = periodData[periodData.length - 1];
              const stratPct = (((last.Strategy   - 10000) / 10000) * 100).toFixed(1);
              const benchPct = (((last['S&P 500'] - 10000) / 10000) * 100).toFixed(1);
              const performanceBlock =
                `\n\nPERIOD PERFORMANCE JUST DISPLAYED:\n` +
                `From ${fromDate} to ${toDate}\n` +
                `Strategy:  $10,000 → $${last.Strategy.toLocaleString()} (${stratPct}%, annualised ${periodStats.annualizedReturn.toFixed(1)}%)\n` +
                `S&P 500:   $10,000 → $${last['S&P 500'].toLocaleString()} (${benchPct}%)\n` +
                `Strategy max drawdown (period): ${periodStats.maxDrawdown.toFixed(1)}%\n` +
                `Strategy Sharpe (period): ${periodStats.sharpe.toFixed(2)}\n\n` +
                `Respond by calling respond_to_client (no parameter fields). Briefly frame the period (what was happening in markets), summarise how the strategy performed vs. the benchmark, and note whether the strategy added value (e.g. outperformance during a drawdown, muted upside during a rally). 3–4 sentences.`;
              const analysisSystem = curConfig.systemPrompt + buildStateContext(curParams, curStats) + buildMemoryContext(memoryRef.current) + performanceBlock;
              const userStart = next.findIndex(m => m.role === 'user');
              const analysisMsgs = next.slice(userStart).map(m => ({
                role: m.role === 'ai' ? 'assistant' : 'user',
                content: m.text,
              }));
              const { input: analysisInput } = await callClaude(analysisSystem, analysisMsgs, [ADVISOR_TOOL]);
              pushAi(analysisInput.message);
            } catch (anErr) {
              console.error('Performance analysis error:', anErr);
              pushAi(`Here's how the strategy performed vs. S&P 500 from ${fromDate} to ${toDate}.`);
            }
          }
        } catch (err) {
          console.error('Performance error:', err);
          pushAi(`⚠️ I couldn't show that performance period: ${err.message}`);
        }
        setPhase(fallbackPhase);
        inputRef.current?.focus();
        return aiReply;
      }

      if (toolName === 'show_holdings' || toolName === 'show_sectors') {
        try {
          const isCurrent = input.isCurrent === true;
          let loadDate = input.date;
          if (isCurrent) {
            const dates = await getDates();
            loadDate = dates[dates.length - 1];
          }
          const { longs, shorts } = await loadHoldings(loadDate);
          const displayDate = isCurrent ? todayIsoLocal() : loadDate;
          const longPct  = 100 - curParams.shortPercent - curParams.goldPercent - curParams.bondsPercent;
          const shortPct = curParams.shortPercent;
          const dateLabel = new Date(displayDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

          if (toolName === 'show_holdings') {
            setHoldingsModal({ view: 'holdings', date: displayDate, longs, shorts, longPct, shortPct });
            try {
              const fmtRow = (r, sleevePct) => `  - ${r.name} (${r.ticker}): ${(r.weight * sleevePct).toFixed(1)}% of portfolio`;
              const holdingsBlock =
                `\n\nHOLDINGS JUST LOADED:\nThe client asked to see portfolio holdings; the app has opened a modal showing the following positions${isCurrent ? ` as of today (${displayDate})` : ` as of ${displayDate}`}.\n\n` +
                `Long basket (${longPct}% of portfolio):\n${longs.map(r => fmtRow(r, longPct)).join('\n')}\n\n` +
                `Short basket (${shortPct}% of portfolio):\n${shorts.map(r => fmtRow(r, shortPct)).join('\n')}\n\n` +
                `Respond by calling respond_to_client (no parameter fields). Your message should:\n` +
                `  • ${isCurrent ? `reference the portfolio as of today (${displayDate}); do NOT mention that it is based on a prior snapshot` : `briefly acknowledge the date ${displayDate}`}\n` +
                `  • refer to companies by their full name only (e.g. "McDonald's"), NEVER by ticker symbol (never "MCD")\n` +
                `  • comment on dominant sectors in each basket\n` +
                `  • characterize the overall positioning as more defensive (staples, utilities, healthcare, bond proxies) vs more aggressive (cyclicals, tech/growth, high-beta)\n` +
                `Keep it to 3–4 sentences.`;
              const analysisSystem = curConfig.systemPrompt + buildStateContext(curParams, curStats) + buildMemoryContext(memoryRef.current) + holdingsBlock;
              const userStart = next.findIndex(m => m.role === 'user');
              const analysisMsgs = next.slice(userStart).map(m => ({
                role: m.role === 'ai' ? 'assistant' : 'user',
                content: m.text,
              }));
              const { input: analysisInput } = await callClaude(analysisSystem, analysisMsgs, [ADVISOR_TOOL]);
              pushAi(analysisInput.message);
            } catch (anErr) {
              console.error('Holdings analysis error:', anErr);
              pushAi(`Here are the long and short baskets as of ${dateLabel}.`);
            }
          } else {
            try {
              const classifyInput = await classifyHoldings(longs, shorts, displayDate);
              const longSectors  = aggregateSectors(classifyInput.long_classifications  || [], longs,  longPct);
              const shortSectors = aggregateSectors(classifyInput.short_classifications || [], shorts, shortPct);
              setHoldingsModal({ view: 'sectors', date: displayDate, longSectors, shortSectors, longPct, shortPct });
              pushAi(classifyInput.message);
            } catch (clErr) {
              console.error('Sector classification error:', clErr);
              pushAi(`⚠️ I couldn't generate a sector breakdown for ${dateLabel}: ${clErr.message}`);
            }
          }
        } catch (loadErr) {
          console.error('Holdings load error:', loadErr);
          pushAi(`⚠️ I couldn't load holdings for that date: ${loadErr.message}`);
        }
        setPhase(fallbackPhase);
        inputRef.current?.focus();
        return aiReply;
      }

      // toolName === 'respond_to_client'
      pushAi(input.message);

      const clamp = (v, lo, hi) => Math.min(Math.max(Math.round(v), lo), hi);
      const hasProfile = typeof input.profile === 'string' && RISK_PROFILES[input.profile];
      const hasTilts   = input.tilts && typeof input.tilts === 'object';
      const hasRaw =
        typeof input.grossExposure === 'number' &&
        typeof input.shortPercent  === 'number' &&
        typeof input.goldPercent   === 'number' &&
        typeof input.bondsPercent  === 'number';

      if (hasProfile || hasTilts) {
        // Preferred path — profile + tilts. If profile is set, tilts are
        // fresh from baseline; otherwise tilts replace the active profile's
        // tilts (or, if no active profile, default to 'balanced').
        const nextProfile = hasProfile ? input.profile : (activeProfile || 'balanced');
        const sentTilts = hasTilts ? input.tilts : null;
        const baseTilts = (hasProfile || !activeProfile) ? ZERO_TILTS : profileTilts;
        const nextTilts = sentTilts
          ? {
              shortPercent: clamp(sentTilts.shortPercent ?? baseTilts.shortPercent ?? 0, -10, 10),
              goldPercent:  clamp(sentTilts.goldPercent  ?? baseTilts.goldPercent  ?? 0, -10, 10),
              bondsPercent: clamp(sentTilts.bondsPercent ?? baseTilts.bondsPercent ?? 0, -10, 10),
            }
          : { ...baseTilts };
        const p = paramsFromProfileAndTilts(nextProfile, nextTilts);
        applyParams(p, { profileKey: nextProfile, tilts: nextTilts });
        if (curParams === null) navigate('/strategy');
      } else if (hasRaw) {
        // Power-user escape — raw numbers. Flips dial to Custom.
        const gross = clamp(input.grossExposure, 0, 200);
        const sp    = clamp(input.shortPercent,  0, 100);
        const gp    = clamp(input.goldPercent,   0, 100);
        const bp    = clamp(input.bondsPercent,  0, 100);
        const total = sp + gp + bp;
        const p = total > 100
          ? { grossExposure: gross, shortPercent: Math.round(sp*98/total), goldPercent: Math.round(gp*98/total), bondsPercent: Math.round(bp*98/total) }
          : { grossExposure: gross, shortPercent: sp, goldPercent: gp, bondsPercent: bp };
        applyParams(p, { profileKey: null, tilts: ZERO_TILTS });
        if (curParams === null) navigate('/strategy');
      } else {
        setPhase(fallbackPhase);
        inputRef.current?.focus();
      }
    } catch (err) {
      console.error('Claude advisor error:', err);
      pushAi('⚠️ I had trouble connecting. Please try again.');
      setPhase(fallbackPhase);
    }
    // Periodic auto-extract — every N user messages, refresh durable memory.
    strategyMsgsSinceExtractRef.current++;
    if (strategyMsgsSinceExtractRef.current >= EXTRACT_EVERY) {
      strategyMsgsSinceExtractRef.current = 0;
      const latest = [...next, { role: 'ai', text: aiReply }];
      autoExtractMemory(latest, 'strategy');
    }
    return aiReply;
  }

  function send() { sendMessage(input.trim(), messages, phase, params, stats, config); }
  function sendDirect(text) { sendMessage(text, messages, phase, params, stats, config); }

  // ── Voice-onboarding handoff ─────────────────────────────────────────────
  // Called by the Advisor page when the ElevenLabs voice agent finishes. The
  // full transcript is sent to Opus, which extracts strategy parameters,
  // drops the user onto the Strategy page, and seeds the chat with the
  // transcript + its own intro so the conversation feels continuous.
  async function bootstrapFromTranscript(transcript) {
    const cfg = configRef.current;
    if (!cfg || !sleeveDataRef.current) throw new Error('Still loading — try again in a moment.');
    if (!transcript || transcript.length === 0) throw new Error('No transcript captured.');

    const transcriptText = transcript
      .map(t => (t.role === 'user' ? 'Client' : 'Voice Advisor') + ': ' + t.text)
      .join('\n');

    const system = cfg.systemPrompt +
      `\n\nBOOTSTRAP MODE:\nThe client has just completed a voice-onboarding session and is now in front of the Strategy page. From the client\'s perspective the conversation is ONE continuous flow — the voice phase already happened, you are the same advisor continuing seamlessly with the strategy phase. The full voice transcript is below; use it to select the strategy.\n\nYou MUST call respond_to_client with a \`profile\` (one of conservative / balanced / growth / aggressive) and optionally \`tilts\` (each in -10..+10 pp), plus a message. Choose the profile based on the client\'s risk tolerance and horizon (use the PROFILE MAPPING GUIDELINES). Use tilts only when the client expressed a specific preference (e.g. inflation worry → +3 gold tilt, strong income focus → +3 bonds tilt, dislike of short-selling → -10 short tilt).\n\nThe \`message\` is the first thing the client sees on the Strategy page. They\'ve never seen this layout before, so it doubles as a strategy hand-off AND a guided primer of what they\'re looking at. Use GitHub-flavored markdown (bold for section headers); the chat bubble renders it.\n\nCONTINUITY RULES (read carefully — this is the most common point of UX friction):\n  • DO NOT greet the client. No "Hi", "Hello", "Hi <name>", "Welcome", "Great to meet you", or any opener of that shape — the greeting already happened during the voice phase.\n  • DO NOT refer to the voice phase as a separate event or to the voice agent by name. Phrases like "after your conversation with <name>", "based on what you told <name>", "now that you\'re done with onboarding", "the team has handed you over to me", or any framing that suggests two distinct advisors / two distinct sessions are BANNED.\n  • Speak in the first person as the single ongoing advisor. The voice transcript is your own memory of the conversation you\'ve been having — reflect themes from it naturally ("you mentioned…", "given your <X> horizon…") rather than attributing them to a third party.\n  • Open straight with the strategy decision in the same tone the voice phase ended in.\n\nStructure:\n\n1. **Decision** (2 sentences, NO greeting): name the recommended profile and the plain-English reason, briefly weaving in the key themes you heard (horizon, risk tolerance, any specific preferences). The very first words must be substantive — not a salutation, not a transition phrase.\n\n2. **Page primer — three short paragraphs, each with a bolded lead-in**, walking the client through the main content area to their LEFT. Keep each paragraph to 1–2 sentences, plain-spoken, no jargon:\n   • **Strategy Mix (top-left).** A profile dial picks one of four pre-set risk levels at any time, and the sliders below nudge specific exposures — drop hedge for protection on the way down, gold against inflation, bonds for steadier income. The colored bar at the bottom shows the resulting mix.\n   • **What this means for you (top-right).** Three plain-English readouts based on a 16-year backtest: what their starting amount would have grown to, the worst stretch the strategy went through (and how long it took to recover), and how steady the ride was vs the S&P 500. Past performance doesn\'t guarantee future results.\n   • **Historical performance (bottom).** A 16-year growth chart comparing the strategy against the S&P 500, both starting at the same dollar amount.\n\n3. **What else they can ask you** (1 sentence): they can also ask to see specific holdings on a given date, review the sector mix, or zoom into performance over a particular period (e.g. during COVID or 2022).\n\n4. **Closing question** (1 sentence): one warm, open question inviting the next action.\n\nLanguage rules: avoid jargon ("drawdown", "Sharpe", "volatility", "gross exposure", "leverage"). Refer to companies by full name only. Address the user with "you", not "the client". Total length: roughly 10–14 sentences. Tone: warm, plain-spoken, confident — like a knowledgeable friend giving you the lay of the land, not a textbook.`;

    const bootstrapMsgs = [{
      role: 'user',
      content: `Here is the transcript of my voice onboarding:\n\n${transcriptText}\n\nPlease build my strategy based on this.`,
    }];

    // Use Opus here — this is the only high-stakes parameter-extraction call.
    const { input: result } = await callClaude(system, bootstrapMsgs, [ADVISOR_TOOL], CLAUDE_MODEL_REASONING);

    const clamp = (v, lo, hi) => Math.min(Math.max(Math.round(v), lo), hi);
    const hasProfile = typeof result.profile === 'string' && RISK_PROFILES[result.profile];
    const hasRaw =
      typeof result.grossExposure === 'number' &&
      typeof result.shortPercent  === 'number' &&
      typeof result.goldPercent   === 'number' &&
      typeof result.bondsPercent  === 'number';

    let p, profileKey = null, tilts = ZERO_TILTS;
    if (hasProfile) {
      profileKey = result.profile;
      const t = result.tilts || {};
      tilts = {
        shortPercent: clamp(t.shortPercent ?? 0, -10, 10),
        goldPercent:  clamp(t.goldPercent  ?? 0, -10, 10),
        bondsPercent: clamp(t.bondsPercent ?? 0, -10, 10),
      };
      p = paramsFromProfileAndTilts(profileKey, tilts);
    } else if (hasRaw) {
      const gross = clamp(result.grossExposure, 0, 200);
      const sp    = clamp(result.shortPercent,  0, 100);
      const gp    = clamp(result.goldPercent,   0, 100);
      const bp    = clamp(result.bondsPercent,  0, 100);
      const total = sp + gp + bp;
      p = total > 100
        ? { grossExposure: gross, shortPercent: Math.round(sp*98/total), goldPercent: Math.round(gp*98/total), bondsPercent: Math.round(bp*98/total) }
        : { grossExposure: gross, shortPercent: sp, goldPercent: gp, bondsPercent: bp };
    } else {
      throw new Error('Could not extract strategy parameters from the conversation.');
    }

    const bt = calcBacktest(sleeveDataRef.current, p);
    const st = calcStats(bt);

    // Seed the Strategy-page chat with the voice transcript + Opus intro so
    // the client sees one continuous thread.
    const chatHistory = [
      ...transcript.map(t => ({ role: t.role, text: t.text })),
      { role: 'ai', text: result.message },
    ];

    setMessages(chatHistory);
    setParams(p);
    setActiveProfile(profileKey);
    setProfileTilts(tilts);
    setBacktestData(bt);
    setStats(st);
    setPhase('results');
    navigate('/strategy');
    // Caller decides whether to auto-start voice with the Opus intro.
    return result.message;
  }

  // Returning-user fast path: rebuild the deployed strategy directly from
  // persisted memory — no Opus bootstrap, no fresh interview. Used by the
  // WelcomeBackView's "Continue to Home" button so the user lands on a
  // populated Home dashboard instead of an empty one. Falls back to
  // bypassOnboarding() if memory is missing the strategy fields (e.g. user
  // engaged but never deployed last session).
  async function resumeFromMemory() {
    const m = memoryRef.current || {};
    const profileKey = m.profile && RISK_PROFILES[m.profile] ? m.profile : null;
    if (!profileKey || !configRef.current || !sleeveDataRef.current) {
      // Not enough state to reconstruct a deployed strategy — fall back to
      // the regular onboarding-bypass path so the user still gets *something*.
      return bypassOnboarding();
    }
    const tilts = {
      shortPercent: clampTilt(m.tilts?.shortPercent),
      goldPercent:  clampTilt(m.tilts?.goldPercent),
      bondsPercent: clampTilt(m.tilts?.bondsPercent),
    };
    const p = paramsFromProfileAndTilts(profileKey, tilts);
    const bt = calcBacktest(sleeveDataRef.current, p);
    const st = bt.length ? calcStats(bt) : null;

    setParams(p);
    setActiveProfile(profileKey);
    setProfileTilts(tilts);
    setBacktestData(bt);
    setStats(st);
    if (typeof m.allocationUsd === 'number' && m.allocationUsd > 0) {
      setAllocationUsd(m.allocationUsd);
    }
    setMessages([]);          // chat history will populate when user engages
    setHomeMessages([]);      // homeOpening seeds via the deployed-effect
    setStrategyEngaged(false);
    setHomeEngaged(false);
    setPhase('deployed');
    setDeployed(true);
  }

  function clampTilt(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(-10, Math.min(10, Math.round(n)));
  }

  // Testing shortcut — skip voice onboarding and feed a canned preference
  // statement to Opus, then drop the user onto the Strategy page with a built
  // strategy. No voice auto-start here — ideal for fast iterations.
  async function bypassOnboarding() {
    const fakeTranscript = [{
      role: 'user',
      text: 'User wants long-term capital growth with 10+ years investment horizon. Ok with 10-15% draw-downs. Also ok with bonds, gold and short positions, but no more than 10% in gold, 20% in bonds and 40% in shorts.',
    }];
    setIsBootstrapping(true);
    setBootstrapError('');
    try {
      await bootstrapFromTranscript(fakeTranscript);
    } catch (err) {
      console.error('Bypass onboarding error:', err);
      setBootstrapError(err?.message || 'Could not build your strategy.');
    } finally {
      setIsBootstrapping(false);
    }
  }
  function handleKey(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }

  // ── TTS (ElevenLabs) ─────────────────────────────────────────────────────

  // ── HeyGen LiveAvatar (onboarding) ───────────────────────────────────────
  // We mint a per-session token here; <Advisor> mounts the SDK provider with
  // the token, attaches the video stream, and feeds chat history back via
  // recordOnboardingMessage / finalizeOnboardingTranscript.

  // ── HeyGen Custom-TTS voice import (chat-mode lip-sync) ─────────────────
  // For chat mode we want HeyGen to do TTS server-side using each advisor's
  // ElevenLabs voice — the same voice Convai uses in onboarding. Two
  // one-time steps wired here:
  //   1. POST /v1/secrets to register our TTS-scope ElevenLabs key with
  //      HeyGen → returns a secret_id. Cached in localStorage.
  //   2. POST /v1/voices/third_party with { secret_id, voice_id (EL) } →
  //      returns a HeyGen voice UUID we pass as avatar_persona.voice_id.
  //      Cached per-advisor in localStorage.
  // With those in place, session.repeat(text) makes the avatar lip-sync to
  // server-side TTS in the right voice — no client-side blob needed.
  // Two secrets, two cache slots: HeyGen stores each ElevenLabs key under
  // its own UUID. 'tts' is the regular voice key for session.repeat()-driven
  // chat avatars; 'convai' is the Convai agent key for the onboarding flow.
  const SECRET_CACHE_KEYS = {
    tts:    'puro-heygen-tts-secret-id',
    convai: 'puro-heygen-convai-secret-id',
  };
  const voiceCacheKey = (advisorId) => `puro-heygen-voice-${advisorId}`;

  async function ensureLiveAvatarSecretRegistered(purpose = 'tts') {
    const cacheKey = SECRET_CACHE_KEYS[purpose];
    if (!cacheKey) throw new Error(`Unknown LiveAvatar secret purpose: ${purpose}`);
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
    // The api-proxy Lambda does the whole registration — it has the
    // LiveAvatar key (for auth) and both ElevenLabs keys (the values
    // being stored) in its env. The browser never sees any of them.
    const res = await apiFetch('/api/register-tts-secret', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ purpose }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`LiveAvatar secret register (${purpose}) ${res.status}: ${text}`);
    }
    const json = await res.json();
    const secretId = json?.secret_id;
    if (!secretId) throw new Error(`LiveAvatar secret register (${purpose}): no secret_id in response ${JSON.stringify(json).slice(0, 300)}`);
    localStorage.setItem(cacheKey, secretId);
    return secretId;
  }

  async function ensureAdvisorVoiceImported(advisor) {
    const cacheKey = voiceCacheKey(advisor.id);
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
    const elVoiceId = advisorVoiceIdsRef.current[advisor.id];
    if (!elVoiceId) throw new Error(`No ElevenLabs voice resolved for ${advisor.name} yet — try again in a second.`);
    const secretId = await ensureLiveAvatarSecretRegistered('tts');
    const res = await apiFetch('/api/liveavatar/v1/voices/third_party', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The API expects `provider_voice_id` (the ElevenLabs voice id we
      // want HeyGen to mirror), not `voice_id` — the docs page calls it
      // `voice_id` but the actual schema uses the more explicit name.
      body: JSON.stringify({ secret_id: secretId, provider_voice_id: elVoiceId }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Voice import ${res.status}: ${text}`);
    }
    const json = await res.json();
    const heygenVoiceId =
      json?.data?.voice_id || json?.data?.id || json?.voice_id || json?.id;
    if (!heygenVoiceId) throw new Error(`Voice import: no voice_id in response ${JSON.stringify(json).slice(0, 300)}`);
    localStorage.setItem(cacheKey, heygenVoiceId);
    console.log('[liveavatar] imported voice for advisor=%s heygenVoiceId=%s', advisor.id, heygenVoiceId);
    // HeyGen returns the voice_id as soon as its record is created, but the
    // voice isn't necessarily usable in a live session for another second or
    // two — the first session.repeat() against a freshly imported voice
    // silently drops (HeyGen publishes an empty muted audio track but never
    // fires AVATAR_SPEAK_STARTED). A short settle delay lets the provision
    // complete server-side before the token is minted. Only applies on the
    // fresh-import path; subsequent sessions hit the localStorage cache
    // above and skip this cost entirely.
    await new Promise(r => setTimeout(r, 2000));
    return heygenVoiceId;
  }

  // kind === 'onboarding' → LITE + ElevenLabs Convai connector. The agent is
  // the brain, gathers info, drives the conversation. Voice comes from the
  // Convai agent's own config.
  // kind === 'chat'       → FULL mode + avatar_persona pinned to the
  // HeyGen-imported per-advisor ElevenLabs voice. We drive the avatar with
  // session.repeat(text) — HeyGen does TTS server-side using our voice and
  // streams the lip-synced result over LiveKit. STT remains ElevenLabs
  // Scribe via MediaRecorder (see startListening).
  async function createLiveAvatarSession(kind = 'onboarding') {
    setLiveAvatarError('');
    setLiveAvatarLoading(true);
    try {
      const advisor = ADVISORS[selectedAdvisorIdx] || ADVISORS[0];
      let body;
      if (kind === 'onboarding') {
        const convaiSecretId = await ensureLiveAvatarSecretRegistered('convai');
        body = {
          mode: 'LITE',
          avatar_id: advisor.avatarId,
          elevenlabs_agent_config: {
            secret_id: convaiSecretId,
            agent_id:  advisor.agentId,
          },
        };
      } else {
        // Best-effort voice import. Fallback to default avatar voice if any
        // step fails (free-tier key, network blip, voice not yet resolved)
        // so the session still works rather than crashing.
        let heygenVoiceId = null;
        try {
          heygenVoiceId = await ensureAdvisorVoiceImported(advisor);
        } catch (e) {
          console.warn('[chat-avatar] voice import failed, using default avatar voice:', e?.message || e);
        }
        body = {
          mode: 'FULL',
          avatar_id: advisor.avatarId,
          avatar_persona: {
            ...(heygenVoiceId ? { voice_id: heygenVoiceId } : {}),
            language: 'en',
          },
        };
      }
      console.log('[liveavatar] token request kind=%s body=%o', kind, body);
      const res = await apiFetch('/api/liveavatar/v1/sessions/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error('[liveavatar] token request failed status=%d body=%s', res.status, text);
        throw new Error(`LiveAvatar token ${res.status}: ${text}`);
      }
      const json = await res.json();
      const token = json?.data?.session_token;
      if (!token) throw new Error('LiveAvatar returned no session_token.');
      return token;
    } catch (e) {
      setLiveAvatarError(e?.message || 'Could not start avatar session.');
      throw e;
    } finally {
      setLiveAvatarLoading(false);
    }
  }

  // Append a single chat-history entry from the LiveAvatar SDK into the
  // running onboarding transcript. Called from <Advisor> as useChatHistory()
  // updates flow in.
  function recordOnboardingMessage(role, text) {
    if (voiceActivePageRef.current !== 'onboarding') return;
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    const last = transcriptRef.current[transcriptRef.current.length - 1];
    if (last && last.role === role && last.text === trimmed) return;  // idempotent
    transcriptRef.current = [...transcriptRef.current, { role, text: trimmed }];
    setVoiceTranscript(transcriptRef.current);
  }

  // Called by <Advisor> when the LiveAvatar session disconnects (user-driven
  // end, server hangup, or unmount). Triggers the same Opus bootstrap → voice
  // handoff path that the old ElevenLabs onDisconnect ran.
  function finalizeOnboardingTranscript() {
    const wasPage = voiceActivePageRef.current;
    voiceActivePageRef.current = null;
    setVoiceActivePage(null);
    setLiveAvatarToken('');
    if (wasPage !== 'onboarding') return;
    const t = transcriptRef.current;
    if (t.length === 0) return;
    setIsBootstrapping(true);
    setBootstrapError('');
    // Pre-mint the chat avatar token in parallel with the Opus bootstrap.
    // The token mint runs ensureAdvisorVoiceImported (HeyGen voice import)
    // + a token POST — typically 1–3 sec, sometimes longer. Without this
    // pre-mint that latency stacks on top of the Opus call, blowing past
    // the waitForChatAvatarReady budget and forcing the bootstrap intro
    // through the ElevenLabs fallback while the avatar arrives just too
    // late to drive it. Overlapping the two saves the stack-up.
    mintChatAvatarTokenIfNeeded();
    bootstrapFromTranscript(t)
      .then((firstMessage) => {
        setIsBootstrapping(false);
        // Seamless voice handoff — start the Strategy voice and speak the
        // Opus-generated intro through the inline ElevenLabs TTS path.
        setTimeout(() => {
          startStrategyVoice(firstMessage).catch(err => console.error('Auto voice start failed:', err));
        }, 300);
      })
      .catch(err => {
        console.error('Bootstrap error:', err);
        setBootstrapError(err?.message || 'Could not build your strategy.');
        setIsBootstrapping(false);
      });
  }

  async function ensureMic() {
    try { await navigator.mediaDevices.getUserMedia({ audio: true }); return true; }
    catch (e) { console.error('Mic denied:', e); return false; }
  }

  // ── Inline ElevenLabs TTS (Strategy / Home) ──────────────────────────────

  function cancelSpeech() {
    // Invalidate any in-flight speakText so none of its late completion paths
    // (a TTS fetch that resolves after cancel, a dangling chunk loop) can
    // play audio or fire its onEnd → startListening.
    speakSeqRef.current++;
    if (speakWatchdogRef.current) {
      clearTimeout(speakWatchdogRef.current);
      speakWatchdogRef.current = null;
    }
    chunkedSpeechActiveRef.current = false;
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    // Avatar interrupt — cancels any in-flight repeat() on the LiveAvatar
    // session. Does nothing if there's no active chat session.
    const chatSession = chatAvatarSessionRef.current;
    if (chatSession && chatAvatarReadyRef.current) {
      try { chatSession.interrupt(); } catch {}
    }
    pendingAvatarSpeakEndRef.current = null;
    pendingAvatarSpeakStartedRef.current = null;
  }

  // Idempotently mint a chat-mode LiveAvatar token. Safe to call multiple
  // times — repeated calls during an in-flight mint are no-ops, and a call
  // when a token already exists is also a no-op. Called both as a pre-mint
  // in finalizeOnboardingTranscript (so the HeyGen + WebRTC handshake
  // overlaps with the Opus bootstrap LLM call rather than running
  // sequentially after it) and as the regular mint in startStrategyVoice
  // when the user starts voice manually.
  function mintChatAvatarTokenIfNeeded() {
    if (chatTokenMintingRef.current)   return;
    if (chatLiveAvatarTokenRef.current) return;
    chatTokenMintingRef.current = true;
    const t0 = Date.now();
    createLiveAvatarSession('chat')
      .then(t => {
        console.log('[chat-avatar] token minted in %dms', Date.now() - t0);
        chatLiveAvatarTokenRef.current = t;
        setChatLiveAvatarToken(t);
      })
      .catch(err => {
        console.warn('[chat-avatar] token mint failed — falling back to ElevenLabs:', err);
      })
      .finally(() => {
        chatTokenMintingRef.current = false;
      });
  }

  // Called by ChatLiveAvatar (ChatPanel) when a session is created/torn down,
  // and when key SDK events fire. Routes the chat speech path through the
  // avatar when ready; falls back to ElevenLabs otherwise.
  function registerChatAvatar(session) { chatAvatarSessionRef.current = session; }
  function unregisterChatAvatar(session) {
    if (chatAvatarSessionRef.current === session) chatAvatarSessionRef.current = null;
    chatAvatarReadyRef.current = false;
    pendingAvatarSpeakEndRef.current = null;
    pendingAvatarSpeakStartedRef.current = null;
  }
  function notifyChatAvatarReady(ready) { chatAvatarReadyRef.current = !!ready; }
  function notifyChatAvatarSpeakStarted() {
    const cb = pendingAvatarSpeakStartedRef.current;
    pendingAvatarSpeakStartedRef.current = null;
    if (cb) cb();
  }

  // Set while playChunkedAvatarSpeech is iterating so notifyChatAvatarSpeakEnded
  // doesn't flicker the UI back to 'idle' between chunks. Cleared when the
  // whole sequence finishes (or fails) and the wrapper restores state itself.
  const chunkedSpeechActiveRef = useRef(false);

  // Watchdog: if speakText's completion signal never arrives (avatar
  // SPEAK_ENDED missing, audio hang, SDK bug — see 2026-09
  // chat-session-dies-on-repeat incident), this ref holds the pending timeout
  // id that force-finishes the utterance so the mic re-opens and the UI isn't
  // stuck at "Speaking…". It is a NO-PROGRESS timer, re-armed as speech
  // advances (per avatar chunk, per <audio> duration) — NOT a fixed wall
  // clock. A fixed 30s timer fired in the middle of the ~60–100s bootstrap
  // intro, called onEnd → startListening while the avatar was still talking,
  // and then the real completion called startListening a second time on top
  // of the live recorder, which orphaned the STT run (stuck at "Listening…").
  const speakWatchdogRef = useRef(null);
  // Bumped by cancelSpeech; each speakText captures its value so stale
  // completion paths can tell they've been superseded.
  const speakSeqRef = useRef(0);
  // Watchdog sizing. The speech estimate is deliberately slow (~10 chars/s vs
  // ~14–15 for real TTS) because it only bounds a failure detector.
  const SPEAK_WATCHDOG_STARTUP_MS = 20000;
  const SPEAK_WATCHDOG_SLACK_MS   = 15000;
  const estimateSpeechMs = (s) => Math.ceil((String(s || '').length / 10) * 1000);

  // Polls for chat-avatar readiness up to a timeout. Used by the bootstrap
  // handoff so the first agent message lip-syncs through repeat() rather
  // than falling through to ElevenLabs <audio>. Returns true if ready in
  // time, false otherwise (caller should still proceed with speakText —
  // it will fall back to <audio> gracefully).
  async function waitForChatAvatarReady(timeoutMs = 6000) {
    if (chatAvatarReadyRef.current) return true;
    const start = Date.now();
    while (!chatAvatarReadyRef.current && Date.now() - start < timeoutMs) {
      await new Promise(r => setTimeout(r, 80));
    }
    return chatAvatarReadyRef.current;
  }
  function notifyChatAvatarSpeakEnded() {
    const cb = pendingAvatarSpeakEndRef.current;
    pendingAvatarSpeakEndRef.current = null;
    // Skip the speaking → idle flip if a chunked-speech sequence is still in
    // flight; the wrapper resets state once all chunks finish.
    if (!chunkedSpeechActiveRef.current) {
      setVoiceState(s => s === 'speaking' ? 'idle' : s);
    }
    // Tight gap before re-opening the mic — echo cancellation handles the
    // small avatar-audio tail that may overlap. 80ms is enough for the
    // event to settle without making the handoff feel laggy.
    if (cb) setTimeout(cb, 80);
  }

  // Convert a Blob to a base64 string (no data: prefix). Used to feed
  // ElevenLabs-generated audio into session.repeatAudio().
  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result || '';
        const idx = String(result).indexOf(',');
        resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Chain repeat() calls one after the other for long messages. Each chunk
  // must clear TWO gates: AVATAR_SPEAK_STARTED within SPEAK_STARTED_TIMEOUT_MS
  // (proves HeyGen accepted the command — sometimes it silently drops them,
  // especially the first repeat after a fresh voice import), then
  // AVATAR_SPEAK_ENDED (proves speech finished). On startup timeout we retry
  // the same chunk once with a short backoff — this catches the common case
  // where HeyGen needed another second to finish provisioning the voice
  // server-side. If the retry also silently fails we throw, and speakText's
  // catch converts that into a fall-through to the ElevenLabs <audio> path
  // (audio still plays, avatar sits idle). chunkedSpeechActiveRef suppresses
  // the per-chunk speaking→idle UI flip while the loop runs.
  const SPEAK_STARTED_TIMEOUT_MS = 3000;
  const SPEAK_STARTED_RETRY_DELAY_MS = 500;
  async function playChunkedAvatarSpeech(chatSession, chunks, { onAttempt, onChunkStarted } = {}) {
    const attemptChunk = (chunkText, chunkIdx, attempt) => new Promise((resolve, reject) => {
      let startedTimer = null;
      // Progress hooks let speakText size its watchdog per chunk instead of
      // using one fixed budget for the whole (possibly very long) message.
      onAttempt?.();
      pendingAvatarSpeakStartedRef.current = () => {
        if (startedTimer) { clearTimeout(startedTimer); startedTimer = null; }
        onChunkStarted?.(chunkText);
      };
      pendingAvatarSpeakEndRef.current = () => resolve();
      try {
        chatSession.repeat(chunkText);
        startedTimer = setTimeout(() => {
          if (pendingAvatarSpeakStartedRef.current) {
            pendingAvatarSpeakStartedRef.current = null;
            pendingAvatarSpeakEndRef.current = null;
            reject(new Error(`HeyGen didn't emit AVATAR_SPEAK_STARTED within ${SPEAK_STARTED_TIMEOUT_MS}ms for chunk ${chunkIdx + 1}/${chunks.length} (attempt ${attempt}) — assuming dropped`));
          }
        }, SPEAK_STARTED_TIMEOUT_MS);
      } catch (e) {
        pendingAvatarSpeakStartedRef.current = null;
        pendingAvatarSpeakEndRef.current = null;
        reject(e);
      }
    });

    chunkedSpeechActiveRef.current = true;
    try {
      for (let i = 0; i < chunks.length; i++) {
        try {
          await attemptChunk(chunks[i], i, 1);
        } catch (e) {
          console.warn('[tts] chunk %d attempt 1 dropped, retrying in %dms', i + 1, SPEAK_STARTED_RETRY_DELAY_MS, e?.message || e);
          await new Promise(r => setTimeout(r, SPEAK_STARTED_RETRY_DELAY_MS));
          // Second attempt uses the same startup-timeout guard; if this also
          // times out, propagate to speakText's catch (ElevenLabs fallback).
          await attemptChunk(chunks[i], i, 2);
        }
      }
    } finally {
      chunkedSpeechActiveRef.current = false;
    }
  }

  // Strip markdown noise that HeyGen's TTS would otherwise read aloud
  // (asterisks for bold, hashes for headers, backticks for code, etc.) and
  // collapse whitespace into single spaces. Avatars need clean prose.
  function sanitizeForAvatar(text) {
    let s = String(text || '');
    // Bold/italic markers
    s = s.replace(/\*\*(.+?)\*\*/g, '$1');
    s = s.replace(/__(.+?)__/g, '$1');
    s = s.replace(/(?<!\w)\*(?!\s)(.+?)(?<!\s)\*(?!\w)/g, '$1');
    s = s.replace(/(?<!\w)_(?!\s)(.+?)(?<!\s)_(?!\w)/g, '$1');
    // Headers
    s = s.replace(/^#{1,6}\s+/gm, '');
    // Inline code
    s = s.replace(/`([^`]+)`/g, '$1');
    // Bullet markers at line start
    s = s.replace(/^\s*[-*•]\s+/gm, '');
    // Paragraph breaks → single space; collapse runs of whitespace
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  }

  async function speakText(text, onEnd, { preferAvatar = true } = {}) {
    console.log('[speakText] enter len=%d preferAvatar=%s hasChatSession=%s chatReady=%s', (text||'').length, preferAvatar, !!chatAvatarSessionRef.current, chatAvatarReadyRef.current);
    cancelSpeech();
    const mySeq = speakSeqRef.current;
    let finished = false;
    const alive = () => !finished && speakSeqRef.current === mySeq;
    setVoiceState('speaking');

    // Single exit for every completion path (avatar done, <audio> ended or
    // errored, TTS failure, watchdog). onEnd re-opens the mic, so it must run
    // AT MOST ONCE per speakText — two startListening calls in a row is what
    // left the Strategy mic stuck at "Listening…" after the handoff.
    const finish = (delayMs) => {
      if (!alive()) return;
      finished = true;
      if (speakWatchdogRef.current) { clearTimeout(speakWatchdogRef.current); speakWatchdogRef.current = null; }
      setVoiceState(s => (s === 'speaking' ? 'idle' : s));
      if (onEnd) setTimeout(() => { if (speakSeqRef.current === mySeq) onEnd(); }, delayMs);
    };

    // No-progress watchdog (see speakWatchdogRef). Re-armed as speech
    // advances; if it ever fires, release whatever path is hung and finish.
    const armWatchdog = (ms, stage) => {
      if (!alive()) return;
      if (speakWatchdogRef.current) clearTimeout(speakWatchdogRef.current);
      const id = setTimeout(() => {
        if (speakWatchdogRef.current !== id) return;
        speakWatchdogRef.current = null;
        if (!alive()) return;
        console.warn('[speakText] watchdog: no progress for %dms during %s — forcing reset', ms, stage);
        pendingAvatarSpeakStartedRef.current = null;
        pendingAvatarSpeakEndRef.current = null;
        chunkedSpeechActiveRef.current = false;
        if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
        finish(0);
      }, ms);
      speakWatchdogRef.current = id;
    };
    armWatchdog(SPEAK_WATCHDOG_STARTUP_MS, 'startup');

    // Avatar path: when the chat-mode session is connected AND the caller
    // hasn't opted out (preferAvatar=false), hand the text to HeyGen via
    // repeat(). HeyGen runs TTS server-side using the imported ElevenLabs
    // voice (same voice as onboarding) and streams a lip-synced result.
    // AVATAR_SPEAK_ENDED → notifyChatAvatarSpeakEnded fires onEnd.
    //
    // preferAvatar=false is a defensive opt-out for callers that want to
    // bypass HeyGen entirely and go straight to the ElevenLabs <audio>
    // fallback below (useful if the chat session is misbehaving on a
    // particular caller). No production caller currently sets it — the
    // watchdog + audio.play catch below are enough to recover cleanly if
    // AVATAR_SPEAK_ENDED never arrives.
    //
    // For long messages we chunk on sentence/paragraph boundaries and chain
    // repeat() calls — HeyGen's command channel is unreliable past ~1200
    // chars (the request goes through but the avatar never speaks).
    const chatSession = chatAvatarSessionRef.current;
    if (preferAvatar && chatSession && chatAvatarReadyRef.current) {
      const clean = sanitizeForAvatar(text);
      const CHUNK_BUDGET = 900;  // safe under any plausible HeyGen ceiling
      try {
        let chunks;
        if (clean.length <= CHUNK_BUDGET) {
          chunks = [clean];
        } else {
          // Chunk on sentence boundaries; pack until the budget is hit.
          const sentences = clean.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [clean];
          chunks = [];
          let buf = '';
          for (const s of sentences) {
            if ((buf + s).length > CHUNK_BUDGET && buf) {
              chunks.push(buf.trim());
              buf = s;
            } else {
              buf += s;
            }
          }
          if (buf.trim()) chunks.push(buf.trim());
          console.log('[speakText] chunking long message into %d chunks (lens=%o)', chunks.length, chunks.map(c => c.length));
        }
        // Unified path — playChunkedAvatarSpeech enforces the per-chunk
        // AVATAR_SPEAK_STARTED timeout so a silent drop of the first repeat()
        // throws instead of hanging, letting us fall through to ElevenLabs.
        // Each repeat() attempt gets the startup budget; once HeyGen confirms
        // it is speaking a chunk, the budget becomes that chunk's estimated
        // duration plus slack — so a long intro can run as long as it needs.
        await playChunkedAvatarSpeech(chatSession, chunks, {
          onAttempt:      () => armWatchdog(SPEAK_WATCHDOG_STARTUP_MS, 'avatar startup'),
          onChunkStarted: (c) => armWatchdog(estimateSpeechMs(c) + SPEAK_WATCHDOG_SLACK_MS, 'avatar chunk'),
        });
        finish(80);
        return;
      } catch (e) {
        // Cancelled, or the watchdog already recovered — don't start a second voice.
        if (!alive()) return;
        console.warn('[tts] repeat failed, falling back to ElevenLabs <audio>:', e);
        pendingAvatarSpeakStartedRef.current = null;
        pendingAvatarSpeakEndRef.current = null;
        // fall through to ElevenLabs blob path
      }
    }

    // Fallback: generate audio via ElevenLabs and play through <audio>. Used
    // during the avatar's warm-up window or if the session is unavailable.
    const idx = selectedAdvisorIdxRef.current ?? 0;
    const advisorId = ADVISORS[idx]?.id;
    const resolvedVoice = advisorVoiceIdsRef.current[advisorId];
    const voiceId = resolvedVoice || EL_VOICE_ID;
    console.log('[tts] fallback advisor=%s voice=%s (resolved=%s)', advisorId, voiceId, !!resolvedVoice);
    armWatchdog(SPEAK_WATCHDOG_STARTUP_MS, 'tts fetch');
    let audioBlob;
    try {
      const res = await apiFetch(`/api/elevenlabs/v1/text-to-speech/${voiceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: (text || '').replace(/\n\n/g, ' '),
          model_id: EL_MODEL,
          voice_settings: { stability: 0.45, similarity_boost: 0.80 },
        }),
      });
      if (!res.ok) throw new Error(`ElevenLabs ${res.status}`);
      audioBlob = await res.blob();
    } catch (err) {
      console.error('[tts] error:', err);
      finish(100);
      return;
    }
    // Superseded (new message / stopVoice) or the watchdog gave up while the
    // TTS was generating — don't start playing a stale utterance.
    if (!alive()) return;
    // Lip-sync via session.repeatAudio(base64) was attempted here but
    // requires HeyGen's session token to include ws_url, which is only
    // returned for LITE + Convai-agent tokens (like onboarding). Our chat
    // token (mode: FULL + avatar_persona, no agent) doesn't get one, so
    // _sessionEventSocket is never created and repeatAudio throws
    // "Cannot repeat audio. Please check you're using a supported mode."
    // Verified on SDK 0.0.17 with live prod traffic 2026-09-29. The avatar
    // stays idle during ElevenLabs playback until either the SDK ships
    // support for command channels without ws_url, or we switch the chat
    // token to a mode HeyGen provisions with ws_url (would likely require
    // adding a stub Convai agent).

    try {
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio(url);
      audioRef.current = audio;
      const release = () => {
        try { URL.revokeObjectURL(url); } catch {}
        if (audioRef.current === audio) audioRef.current = null;
      };
      // Size the watchdog to the actual clip once its duration is known.
      audio.onloadedmetadata = () => {
        const d = audio.duration;
        armWatchdog(
          (Number.isFinite(d) && d > 0 ? Math.ceil(d * 1000) : estimateSpeechMs(text)) + SPEAK_WATCHDOG_SLACK_MS,
          'audio playback',
        );
      };
      audio.onended = () => { release(); finish(300); };
      audio.onerror = (e) => {
        console.warn('[tts] audio element error:', e);
        release();
        finish(0);
      };
      armWatchdog(estimateSpeechMs(text) + SPEAK_WATCHDOG_SLACK_MS, 'audio playback');
      // audio.play() returns a promise that rejects on autoplay-policy
      // blocks. Without a .catch, the rejection is silent and voiceState
      // stays stuck at 'speaking' — the exact "UI says Speaking… but no
      // audio" symptom. Catch it, reset, and open the mic.
      audio.play().catch(err => {
        console.warn('[tts] audio.play() rejected (likely autoplay policy):', err);
        release();
        finish(0);
      });
    } catch (err) {
      console.error('[tts] playback error:', err);
      finish(0);
    }
  }

  // ── ElevenLabs Scribe STT (Strategy / Home) ──────────────────────────────
  // Replaces the browser Web Speech API (which routes through Google's cloud
  // STT and was unreliable: 4-8s warm-up, intermittent `network` errors).
  // Pipeline:
  //   1. getUserMedia → MediaStream → MediaRecorder records the utterance.
  //   2. Web Audio AnalyserNode runs RMS-amplitude VAD on the same stream.
  //   3. End of utterance is declared after SILENCE_MS of below-threshold
  //      audio that follows at least one above-threshold sample. The
  //      recorder is stopped, its blob is uploaded to /v1/speech-to-text,
  //      and the resulting text feeds the existing sendMessage path.
  // We tag each utterance with sttRunIdRef so a stale STT response (user
  // already moved on / stopped voice) is dropped.
  function tearDownListening() {
    if (vadRafRef.current) { try { cancelAnimationFrame(vadRafRef.current); } catch {} vadRafRef.current = 0; }
    if (recorderRef.current) {
      try { if (recorderRef.current.state === 'recording') recorderRef.current.stop(); } catch {}
      recorderRef.current = null;
    }
    if (micStreamRef.current) {
      try { micStreamRef.current.getTracks().forEach(t => t.stop()); } catch {}
      micStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }
  }

  async function transcribeWithElevenLabs(blob) {
    const form = new FormData();
    form.append('file', blob, 'utterance.webm');
    form.append('model_id', 'scribe_v1');
    // Lock the model to English so it doesn't auto-detect Spanish/etc on
    // ambiguous audio, and disable Scribe's non-speech tagging so we don't
    // get "(silence)", "(footsteps)", "(background noise)" in the output.
    form.append('language_code', 'eng');
    form.append('tag_audio_events', 'false');
    // 12s timeout — Scribe usually returns in under 3s for ≤30s audio. If
    // the request hangs (network blip, EL incident), abort so the mic loop
    // doesn't strand the user in "listening" without a follow-up.
    const ac = new AbortController();
    const timeoutId = setTimeout(() => ac.abort(), 12000);
    try {
      // Routed through the api-proxy Lambda which injects xi-api-key
      // server-side. Do NOT set Content-Type — browser sets the
      // multipart boundary.
      const res = await apiFetch('/api/elevenlabs/v1/speech-to-text', {
        method: 'POST',
        body: form,
        signal: ac.signal,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Scribe ${res.status}: ${text}`);
      }
      const json = await res.json();
      return (json?.text || '').trim();
    } catch (e) {
      if (e?.name === 'AbortError') throw new Error('Scribe timed out after 12s');
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  function submitVoice(kind, t) {
    console.log('[stt] submit kind=%s text=%s', kind, JSON.stringify(t));
    noSpeechRestartsRef.current = 0;
    if (kind === 'home') {
      setHomeInput(t);
      homeSendMessage(t, homeMessagesRef.current, homePhaseRef.current, paramsRef.current, statsRef.current, configRef.current);
    } else {
      setInput(t);
      sendMessage(t, messagesRef.current, phaseRef.current, paramsRef.current, statsRef.current, configRef.current);
    }
  }

  async function startListening(kind, { isAutoRestart = false } = {}) {
    // Fresh user-initiated start gets a clean retry budget. Auto-restart
    // chains keep their counter so we don't loop forever on dead mics.
    if (!isAutoRestart) noSpeechRestartsRef.current = 0;
    // Lighter teardown than tearDownListening: we want to reuse the mic
    // stream from the previous turn (if it's still live) to avoid the
    // ~500ms+ Chrome getUserMedia warm-up between conversation turns.
    if (vadRafRef.current) { try { cancelAnimationFrame(vadRafRef.current); } catch {} vadRafRef.current = 0; }
    if (recorderRef.current) {
      try { if (recorderRef.current.state === 'recording') recorderRef.current.stop(); } catch {}
      recorderRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }

    const MAX_RESTARTS = 6;
    // 2.5s of silence before we declare end-of-turn. Shorter (~1.2s) was
    // too snappy: Chrome's noise suppression + auto-gain can dip
    // mid-syllable amplitude below the VAD threshold for a beat, and we
    // were ending the recording while the user was still speaking.
    const SILENCE_MS = 2500;
    const NO_SPEECH_TIMEOUT_MS = 10000;
    const MAX_UTTERANCE_MS     = 45000;
    // RMS amplitude threshold for "voice present". 0.012 catches normal
    // conversational speech without false-triggering on room noise. Quiet
    // breaths typically sit around 0.003–0.006.
    const VOICE_THRESHOLD = 0.012;

    const myRunId = ++sttRunIdRef.current;
    // Reuse the prior turn's mic stream if its tracks are still live; only
    // call getUserMedia on the very first turn or after stopVoice. This
    // turns turn-to-turn handoffs from ~600ms into ~50ms.
    const existing = micStreamRef.current;
    const existingLive =
      existing && existing.getAudioTracks().some(t => t.readyState === 'live');
    let stream;
    if (existingLive) {
      stream = existing;
    } else {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch (e) {
        console.error('[stt] getUserMedia failed:', e);
        setVoiceState('idle');
        return;
      }
      if (sttRunIdRef.current !== myRunId || !voiceActivePageRef.current) {
        // Voice was stopped while waiting on the permission prompt.
        try { stream.getTracks().forEach(t => t.stop()); } catch {}
        return;
      }
      micStreamRef.current = stream;
    }

    let mimeType = 'audio/webm;codecs=opus';
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && !MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
    }
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    recorderRef.current = recorder;
    const chunks = [];
    // This run's own VAD AudioContext (created below). Declared before onstop
    // so onstop releases exactly this context and nothing a newer run owns.
    let audioCtx = null;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    recorder.onstart = () => {
      console.log('[stt] recorder.onstart kind=%s autoRestart=%s mimeType=%s', kind, isAutoRestart, recorder.mimeType);
      setVoiceState('listening');
    };
    recorder.onerror = (e) => { console.warn('[stt] recorder.onerror', e); };
    recorder.onstop = async () => {
      console.log('[stt] recorder.onstop kind=%s chunks=%d', kind, chunks.length);
      // Tear down recorder + audio context, but KEEP the mic stream alive
      // so the next turn can reuse it without paying the getUserMedia cost
      // (~500ms+ on Chrome, especially after WebRTC activity). The stream is
      // released by stopVoice / unmount cleanup via tearDownListening.
      if (audioCtx && audioCtx.state !== 'closed') { try { audioCtx.close().catch(() => {}); } catch {} }

      // Race guard FIRST. MediaRecorder dispatches 'stop' asynchronously, so
      // when startListening() supersedes a run that is still recording, this
      // handler only fires AFTER the newer run has installed its own
      // recorder / AudioContext / VAD loop in the shared refs. Tearing those
      // refs down before this check (as this code used to) cancelled the new
      // run's VAD, closed its AudioContext and dropped its recorder — the UI
      // sat at "Listening…" forever and nothing was ever transcribed.
      if (sttRunIdRef.current !== myRunId) return;
      if (vadRafRef.current) { try { cancelAnimationFrame(vadRafRef.current); } catch {} vadRafRef.current = 0; }
      if (audioCtxRef.current === audioCtx) audioCtxRef.current = null;
      if (recorderRef.current === recorder) recorderRef.current = null;

      const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
      console.log('[stt] blob size=%d type=%s', blob.size, blob.type);
      // Below ~3KB the recording is silence/noise — skip the upload and
      // either auto-restart (still actively listening) or give up.
      const tooSmall = blob.size < 3000;
      let text = '';
      if (!tooSmall) {
        try {
          text = await transcribeWithElevenLabs(blob);
        } catch (err) {
          console.error('[stt] transcribe failed:', err);
        }
      }
      if (sttRunIdRef.current !== myRunId) return;

      // Scribe annotates non-speech audio with parenthetical tags like
      // "(Background noise)", "(Music)", "(Sneezing)" — when the entire
      // transcript is just one of those, the user didn't actually say
      // anything intelligible. Treat as empty so we auto-restart instead
      // of pushing nonsense into Claude's context.
      const looksNonSpeech = /^\s*\([^()]*\)\s*$/.test(text);
      if (text && !looksNonSpeech) {
        submitVoice(kind, text);
        setVoiceState(s => s === 'listening' ? 'idle' : s);
        return;
      }
      if (looksNonSpeech) {
        console.log('[stt] non-speech transcript ignored:', JSON.stringify(text));
      }

      // Empty / failed transcript — soft retry while voice is still active.
      if (voiceActivePageRef.current && noSpeechRestartsRef.current < MAX_RESTARTS) {
        noSpeechRestartsRef.current++;
        console.log('[stt] empty transcript, auto-restart %d/%d', noSpeechRestartsRef.current, MAX_RESTARTS);
        setTimeout(() => {
          // Skip if a fresh startListening already took over in the meantime.
          if (voiceActivePageRef.current && sttRunIdRef.current === myRunId) {
            startListening(kind, { isAutoRestart: true });
          }
        }, 200);
        return;
      }
      noSpeechRestartsRef.current = 0;
      setVoiceState(s => s === 'listening' ? 'idle' : s);
    };

    // Web Audio VAD — runs alongside MediaRecorder on the same stream.
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);

      const startTs = Date.now();
      let lastVoiceTs = startTs;
      let hasSpoken = false;
      const tick = () => {
        if (sttRunIdRef.current !== myRunId) return;  // a newer run owns the mic
        // Use this run's own recorder, not the shared ref, so the VAD can't be
        // blinded by another run's bookkeeping.
        if (recorder.state !== 'recording') return;
        analyser.getByteTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sumSq += v * v; }
        const rms = Math.sqrt(sumSq / buf.length);
        const now = Date.now();
        if (rms > VOICE_THRESHOLD) { lastVoiceTs = now; hasSpoken = true; }

        const elapsed = now - startTs;
        const silenceMs = now - lastVoiceTs;
        if (hasSpoken && silenceMs > SILENCE_MS) {
          try { recorder.stop(); } catch {}
          return;
        }
        if (!hasSpoken && elapsed > NO_SPEECH_TIMEOUT_MS) {
          try { recorder.stop(); } catch {}
          return;
        }
        if (elapsed > MAX_UTTERANCE_MS) {
          try { recorder.stop(); } catch {}
          return;
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn('[stt] VAD init failed, falling back to fixed timeout:', e);
      // Without VAD we still capture — give the user a fixed window.
      setTimeout(() => {
        if (sttRunIdRef.current === myRunId && recorder.state === 'recording') {
          try { recorder.stop(); } catch {}
        }
      }, 6000);
    }

    try {
      recorder.start();
    } catch (e) {
      console.error('[stt] recorder.start failed:', e);
      tearDownListening();
      setVoiceState('idle');
    }
  }

  // Auto-speak the latest AI message when voice is active on that page, then
  // re-open the mic for the next user turn — unless the strategy has just
  // been deployed, in which case we close the voice session entirely. The
  // post-deploy turn is the agent's wrap-up ("happy investing journey…");
  // there's nothing left to say, so leaving the mic open feels awkward.
  useEffect(() => {
    if (voiceActivePageRef.current !== 'strategy') return;
    const idx = messages.length - 1;
    const last = messages[idx];
    console.log('[autospeak/strategy] check idx=%d role=%s lastSpoken=%d', idx, last?.role, lastSpokenRef.current.strategy);
    if (!last || last.role !== 'ai' || idx <= lastSpokenRef.current.strategy) return;
    lastSpokenRef.current.strategy = idx;
    speakText(last.text, () => {
      if (voiceActivePageRef.current !== 'strategy') return;
      if (phaseRef.current === 'deployed') {
        stopVoice();
        return;
      }
      startListening('strategy');
    });
  }, [messages]); // refs intentionally omitted

  useEffect(() => {
    if (voiceActivePageRef.current !== 'home') return;
    const idx = homeMessages.length - 1;
    const last = homeMessages[idx];
    console.log('[autospeak/home] check idx=%d role=%s lastSpoken=%d', idx, last?.role, lastSpokenRef.current.home);
    if (!last || last.role !== 'ai' || idx <= lastSpokenRef.current.home) return;
    lastSpokenRef.current.home = idx;
    speakText(last.text, () => {
      if (voiceActivePageRef.current === 'home') startListening('home');
    });
  }, [homeMessages]); // refs intentionally omitted

  // Fetch each advisor's still preview image once on mount so the carousel
  // can render them instantly. Failures are non-fatal — the UI shows a
  // placeholder for any advisor whose preview can't be fetched.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const previews = {};
      await Promise.all(ADVISORS.map(async (a) => {
        try {
          const res = await apiFetch(`/api/liveavatar/v1/avatars/${a.avatarId}`);
          if (!res.ok) return;
          const json = await res.json();
          const data = json?.data || {};
          const url = data.preview_image_url || data.normal_preview || data.preview_url || '';
          if (url) previews[a.avatarId] = url;
        } catch {}
      }));
      if (!cancelled) setAdvisorPreviews(previews);
    })();
    return () => { cancelled = true; };
  }, []);

  // Look up each advisor's Convai voice id once on mount. The voice id lives
  // on the agent config under `conversation_config.tts.voice_id`. Strategy
  // and Home TTS use it so the voice carries over from onboarding.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const voices = {};
      await Promise.all(ADVISORS.map(async (a) => {
        try {
          // Routed through the api-proxy Lambda which injects xi-api-key
          // (Convai scope) server-side — the auth key no longer lives in
          // the browser bundle.
          const res = await apiFetch(`/api/elevenlabs/v1/convai/agents/${a.agentId}`);
          if (!res.ok) return;
          const json = await res.json();
          const vid = json?.conversation_config?.tts?.voice_id
                   || json?.platform_settings?.tts?.voice_id  // fallback shapes
                   || json?.tts?.voice_id
                   || '';
          if (vid) voices[a.id] = vid;
        } catch {}
      }));
      if (!cancelled) {
        setAdvisorVoiceIds(voices);
        advisorVoiceIdsRef.current = voices;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep selectedAdvisorIdxRef in sync — speakText reads it without going
  // through a re-render so cross-page voice changes apply immediately.
  useEffect(() => {
    selectedAdvisorIdxRef.current = selectedAdvisorIdx;
  }, [selectedAdvisorIdx]);

  // Keep memoryRef in sync so async paths read the latest snapshot.
  useEffect(() => { memoryRef.current = memory; }, [memory]);
  // Keep the conversation/phase/params/stats refs in sync for the voice
  // submit() path, which needs fresh values without nesting setState calls.
  useEffect(() => { messagesRef.current     = messages;     }, [messages]);
  useEffect(() => { phaseRef.current        = phase;        }, [phase]);
  useEffect(() => { paramsRef.current       = params;       }, [params]);
  useEffect(() => { statsRef.current        = stats;        }, [stats]);
  useEffect(() => { homeMessagesRef.current = homeMessages; }, [homeMessages]);
  useEffect(() => { homePhaseRef.current    = homePhase;    }, [homePhase]);

  // ── Memory write helpers (used by deploy + auto-extraction) ─────────────

  function commitMemory(next) {
    saveMemory(next);
    setMemory(next);
    memoryRef.current = next;
  }

  // Update the durable strategy slots (profile / tilts / allocation /
  // advisor) in memory. Idempotent — any null/undefined fields are ignored.
  function updateMemoryStrategy({ profile, tilts, allocationUsd, selectedAdvisor }) {
    const cur = memoryRef.current || {
      version: MEMORY_VERSION,
      firstSession: new Date().toISOString(),
      sessionCount: 0,
      goals: [],
      concerns: [],
      preferences: [],
      recentSessions: [],
      olderHistorySummary: '',
    };
    const next = {
      ...cur,
      version: MEMORY_VERSION,
      firstSession: cur.firstSession || new Date().toISOString(),
      lastSession:  new Date().toISOString(),
      sessionCount: (cur.sessionCount || 0) + (cur.lastSession ? 0 : 1),
      ...(profile          ? { profile }                 : {}),
      ...(tilts            ? { tilts: { ...tilts } }     : {}),
      ...(allocationUsd    ? { allocationUsd }           : {}),
      ...(selectedAdvisor  ? { selectedAdvisor }         : {}),
    };
    commitMemory(next);
  }

  // Periodic auto-extraction: every N user messages, fire a Haiku call to
  // extract any new concerns / preferences / goals from the recent exchange
  // and update the running session digest. Cheap (Haiku) and async — failure
  // is non-fatal (memory just doesn't update this round).
  const homeMsgsSinceExtractRef = useRef(0);
  const strategyMsgsSinceExtractRef = useRef(0);
  const EXTRACT_EVERY = 2;

  async function autoExtractMemory(messages, kind /* 'home' | 'strategy' */) {
    try {
      const tail = (messages || []).slice(-10).map(m => `${m.role}: ${m.text}`).join('\n');
      if (!tail.trim()) return;
      const cur = memoryRef.current || {};
      const today = new Date().toISOString().slice(0, 10);
      const system =
        `You are extracting durable user-memory facts from a recent conversation between a wealth-management AI and a self-directed retail investor. Update the running memory by merging anything NEW expressed in this exchange.\n\n` +
        `CURRENT MEMORY SNAPSHOT:\n` +
        JSON.stringify({
          concerns: cur.concerns || [],
          preferences: cur.preferences || [],
          goals: cur.goals || [],
        }, null, 2) +
        `\n\nRECENT EXCHANGE (${kind} chat):\n${tail}\n\n` +
        `Call update_user_memory with the FULL UPDATED lists (existing items plus any newly stated ones). Each item should be 5-15 words, plain English. Maximum 5 items per category. Drop items that are contradicted by newer statements. Also write a 1-2 sentence sessionDigest covering what was discussed in today's session so far.`;
      const tool = {
        name: 'update_user_memory',
        description: 'Save updated user memory after analyzing the recent exchange.',
        input_schema: {
          type: 'object',
          properties: {
            concerns:      { type: 'array', items: { type: 'string' }, description: 'Things the user is worried about (markets, life events, specific holdings, etc.).' },
            preferences:   { type: 'array', items: { type: 'string' }, description: 'Stated preferences (asset classes, strategies, communication style).' },
            goals:         { type: 'array', items: { type: 'string' }, description: 'Stated financial goals.' },
            sessionDigest: { type: 'string', description: '1-2 sentence summary of today\'s session topics so far.' },
          },
          required: ['concerns', 'preferences', 'goals', 'sessionDigest'],
        },
      };
      const { input } = await callClaude(system, [{ role: 'user', content: 'Extract now.' }], [tool], CLAUDE_MODEL_FAST);
      const cur2 = memoryRef.current || {};
      const recent = [...(cur2.recentSessions || [])];
      const todayIdx = recent.findIndex(s => s.date === today);
      if (todayIdx >= 0) {
        recent[todayIdx] = { ...recent[todayIdx], summary: input.sessionDigest || recent[todayIdx].summary };
      } else {
        recent.push({ date: today, summary: input.sessionDigest || '', followUps: [] });
        while (recent.length > 5) recent.shift();
      }
      const next = {
        ...cur2,
        version: MEMORY_VERSION,
        firstSession: cur2.firstSession || new Date().toISOString(),
        lastSession:  new Date().toISOString(),
        sessionCount: cur2.sessionCount || 1,
        concerns:     Array.isArray(input.concerns)    ? input.concerns.slice(0, 5)    : (cur2.concerns    || []),
        preferences:  Array.isArray(input.preferences) ? input.preferences.slice(0, 5) : (cur2.preferences || []),
        goals:        Array.isArray(input.goals)       ? input.goals.slice(0, 5)       : (cur2.goals       || []),
        recentSessions: recent,
      };
      commitMemory(next);
    } catch (e) {
      console.warn('Memory auto-extract failed:', e);
    }
  }

  // Demo controls
  function resetMemory()    { clearMemoryLS(); setMemory(null); memoryRef.current = null; }
  function seedMemoryNow()  { const seed = buildSeedMemory(); commitMemory(seed); }
  function openMemoryView() { setMemoryViewOpen(true); }
  function closeMemoryView(){ setMemoryViewOpen(false); }
  function openMetricsView() { setMetricsViewOpen(true); }
  function closeMetricsView(){ setMetricsViewOpen(false); }

  async function startOnboardingVoice() {
    // Defensive: release any chat-mode mic stream / Scribe recorder that
    // may still be alive from a prior Strategy/Home voice session, AND tear
    // down any chat-mode LiveAvatar session. Without this, two LiveKit
    // sessions can race for the mic device and Convai sees a silent track.
    sttRunIdRef.current++;
    tearDownListening();
    cancelSpeech();
    setChatLiveAvatarToken('');

    if (!(await ensureMic())) return;
    transcriptRef.current = [];
    setVoiceTranscript([]);
    setBootstrapError('');
    voiceActivePageRef.current = 'onboarding';
    setVoiceActivePage('onboarding');
    try {
      const token = await createLiveAvatarSession();
      setLiveAvatarToken(token);
    } catch (e) {
      // Token failed — roll back the active-page flag.
      voiceActivePageRef.current = null;
      setVoiceActivePage(null);
    }
  }

  async function startStrategyVoice(firstMessageOverride) {
    // Tear down anything currently running. Onboarding tear-down is driven
    // by clearing liveAvatarToken — the SDK provider unmounts and the
    // session disconnects, which calls finalizeOnboardingTranscript().
    if (voiceActivePageRef.current === 'onboarding') {
      setLiveAvatarToken('');
    } else {
      tearDownListening();
      cancelSpeech();
    }
    if (!(await ensureMic())) return;
    voiceActivePageRef.current = 'strategy';
    setVoiceActivePage('strategy');
    // Mark whatever's already on screen as "known" so the auto-speak effect
    // doesn't re-read old messages when the user re-enables voice mid-session.
    lastSpokenRef.current.strategy = messages.length - 1;
    // Mint the chat LiveAvatar token in parallel — idempotent and safe to
    // call even if finalizeOnboardingTranscript already kicked it off
    // (mintChatAvatarTokenIfNeeded is a no-op when a mint is in flight or
    // a token already exists).
    mintChatAvatarTokenIfNeeded();
    if (firstMessageOverride) {
      // Wait for the chat avatar to come up so the handoff message lip-syncs
      // through session.repeat() instead of falling through to the ElevenLabs
      // <audio> path (no lip-sync). 12s budget covers the worst-case HeyGen
      // WebRTC handshake on first connection. The pre-mint kicked off in
      // finalizeOnboardingTranscript means most of this window is already
      // eaten by the parallel Opus bootstrap, so the user-visible wait is
      // usually only a fraction of the cap. If the wait times out, speakText
      // will fall through to the <audio> path — audio still plays, avatar
      // sits idle. Defence-in-depth (watchdog + audio.play catch) covers the
      // scenario where session.repeat is dispatched but AVATAR_SPEAK_ENDED
      // never fires (early hypothesis was that this always broke; live
      // testing showed the session stays alive and lip-syncs correctly).
      const t0 = Date.now();
      const ready = await waitForChatAvatarReady(12000);
      console.log('[chat-avatar] ready=%s waited=%dms hasSession=%s', ready, Date.now() - t0, !!chatAvatarSessionRef.current);
      await speakText(firstMessageOverride, () => {
        if (voiceActivePageRef.current === 'strategy') startListening('strategy');
      });
    } else {
      startListening('strategy');
    }
  }

  async function startHomeVoice() {
    if (voiceActivePageRef.current === 'onboarding') {
      setLiveAvatarToken('');
    } else {
      tearDownListening();
      cancelSpeech();
    }
    if (!(await ensureMic())) return;
    voiceActivePageRef.current = 'home';
    setVoiceActivePage('home');
    lastSpokenRef.current.home = homeMessages.length - 1;
    createLiveAvatarSession('chat')
      .then(t => { console.log('[chat-avatar] token minted'); setChatLiveAvatarToken(t); })
      .catch(err => { console.warn('[chat-avatar] token mint failed — falling back to ElevenLabs:', err); });
    startListening('home');
  }

  async function stopVoice() {
    if (voiceActivePageRef.current === 'onboarding') {
      // Clearing the token unmounts the LiveAvatar provider and tears down
      // the session; finalizeOnboardingTranscript fires from <Advisor>.
      setLiveAvatarToken('');
    } else {
      // Clear voiceActivePage FIRST so the in-flight recorder's onstop
      // handler doesn't try to auto-restart after we've stopped.
      voiceActivePageRef.current = null;
      setVoiceActivePage(null);
      noSpeechRestartsRef.current = 0;
      // Bump the run id so any in-flight Scribe upload's response is dropped.
      sttRunIdRef.current++;
      tearDownListening();
      cancelSpeech();
      setVoiceState('idle');
      // Tear down the chat LiveAvatar (if any). ChatLiveAvatar's cleanup
      // effect (keyed on the token) calls session.stop().
      setChatLiveAvatarToken('');
    }
  }

  // Cleanup on unmount.
  useEffect(() => () => {
    tearDownListening();
    cancelSpeech();
    setLiveAvatarToken('');
    setChatLiveAvatarToken('');
  }, []);

  function deploy() {
    const deployMsg = { role: 'ai', text: 'Your strategy has been queued for deployment and will start trading live once reviewed by our portfolio team and the account has been funded. From then on, the portfolio will automatically rebalance every week — opening new positions and reducing or closing older ones — based on the model parameters you selected. You can review your current holdings, watch real-time performance, and explore what\'s driving the portfolio on the Home screen.' };
    const nextMessages = [...messages, deployMsg];
    setMessages(nextMessages);
    setPhase('deployed');
    setDeployed(true);
    updateMemoryStrategy({
      profile: activeProfile,
      tilts: profileTilts,
      selectedAdvisor: ADVISORS[selectedAdvisorIdxRef.current]?.id,
    });
    autoExtractMemory(nextMessages, 'strategy');
  }

  async function resetConversation() {
    const cfg = await loadConfig();
    setConfig(cfg);
    configRef.current = cfg;
    setMessages([{ role: 'ai', text: cfg.opening }]);
    setParams(null);
    setBacktestData(null);
    setStats(null);
    setPhase('chat');
    setDeployed(false);
    setHomeMessages([]);
    setHomePhase('chat');
    setHomeData(null);
    setStrategyEngaged(false);
    setHomeEngaged(false);
    navigate('/');
  }

  // ── Home data loader ─────────────────────────────────────────────────────

  async function loadHomeData(curParams) {
    if (!sleeveDataRef.current) throw new Error('sleeve data not loaded');
    const dates = await getDates();
    const latestDate = dates[dates.length - 1];
    const [longs, shorts] = await Promise.all([
      loadHoldingsCsv(latestDate, 'long'),
      loadHoldingsCsv(latestDate, 'short'),
    ]);
    const longPct  = 100 - curParams.shortPercent - curParams.goldPercent - curParams.bondsPercent;
    const shortPct = curParams.shortPercent;
    const classifyInput = await classifyHoldings(longs, shorts, latestDate);
    const longSectors  = aggregateSectors(classifyInput.long_classifications  || [], longs,  longPct);
    const shortSectors = aggregateSectors(classifyInput.short_classifications || [], shorts, shortPct);
    const netMap = new Map();
    longSectors.forEach(s  => netMap.set(s.sector, (netMap.get(s.sector) || 0) + s.portfolioPct));
    shortSectors.forEach(s => netMap.set(s.sector, (netMap.get(s.sector) || 0) - s.portfolioPct));
    const netSectors = [...netMap.entries()]
      .map(([sector, net]) => ({ sector, net }))
      .filter(s => Math.abs(s.net) > 0.01)
      .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
    return { latestDate, longs, shorts, longPct, shortPct, longSectors, shortSectors, netSectors };
  }

  // Populate home data + opening message when deployed
  useEffect(() => {
    if (!deployed || !params || !configRef.current) return;
    if (homeMessages.length === 0 && configRef.current.homeOpening) {
      setHomeMessages([{ role: 'ai', text: configRef.current.homeOpening }]);
    }
    let cancelled = false;
    setHomeData(null);
    loadHomeData(params)
      .then(d => { if (!cancelled) setHomeData(d); })
      .catch(e => { if (!cancelled) console.error('Home data load failed:', e); });
    return () => { cancelled = true; };
  }, [deployed, params]);

  useEffect(() => {
    homeBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [homeMessages, homePhase]);

  // ── Home chat send ───────────────────────────────────────────────────────

  // Generic data-fetch handler invoked by the agent's query_historical_data
  // tool calls. Returns a structured payload that's JSON-stringified back to
  // Claude as a tool_result so the model can reason on real numbers and
  // optionally render an inline chart in its follow-up respond_to_client.
  async function runQueryHistoricalData(input) {
    const dataset = input?.dataset;

    if (dataset === 'intraday') {
      const res = await fetch('/intraday_today.csv?t=' + Date.now());
      if (!res.ok) throw new Error(`Intraday CSV unavailable (HTTP ${res.status}). The updater Lambda may not have run yet today.`);
      const text = await res.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const rows = parsed.data || [];
      if (rows.length === 0) return { dataset: 'intraday', error: 'No intraday rows available yet today.' };

      const allCols = Object.keys(rows[0]).filter(c => c && c !== 'timestamp');
      const requested = (Array.isArray(input?.tickers) && input.tickers.length > 0)
        ? input.tickers.filter(t => allCols.includes(t))
        : allCols;

      const priorCloseRow = rows.find(r => r.timestamp === 'prior_close') || null;
      const intradayRows = rows.filter(r => r.timestamp !== 'prior_close');

      const prices = {};
      for (const col of requested) {
        prices[col] = intradayRows.map(r => {
          const v = parseFloat(r[col]);
          return Number.isFinite(v) && v > 0 ? v : null;
        });
      }
      const result = {
        dataset: 'intraday',
        timestamps: intradayRows.map(r => r.timestamp),
        tickers: requested,
        prices,
      };
      if (priorCloseRow) {
        result.priorClose = {};
        for (const col of requested) {
          const v = parseFloat(priorCloseRow[col]);
          if (Number.isFinite(v) && v > 0) result.priorClose[col] = v;
        }
      }
      return result;
    }

    if (dataset === 'backtest') {
      const allRows = backtestData || [];
      if (allRows.length === 0) return { dataset: 'backtest', error: 'Backtest data not yet computed for the current strategy.' };

      const from = input?.fromDate ? new Date(input.fromDate) : null;
      const to   = input?.toDate   ? new Date(input.toDate)   : null;
      let filtered = allRows;
      if (from || to) {
        filtered = allRows.filter(r => {
          const d = new Date(r.date);
          if (from && d < from) return false;
          if (to   && d > to)   return false;
          return true;
        });
      }
      const sample = Math.max(1, parseInt(input?.sampleEvery, 10) || 1);
      if (sample > 1) filtered = filtered.filter((_, i) => i % sample === 0);

      return {
        dataset: 'backtest',
        sampleEvery: sample,
        count: filtered.length,
        firstDate: filtered[0]?.date || null,
        lastDate:  filtered[filtered.length - 1]?.date || null,
        rows: filtered.map(r => ({
          date: r.date,
          Strategy: Math.round(r.Strategy),
          sp500:    Math.round(r['S&P 500']),
        })),
      };
    }

    throw new Error(`Unknown dataset: ${dataset}`);
  }

  async function homeSendMessage(text, curMessages, curPhase, curParams, curStats, curConfig) {
    if (!text || curPhase === 'thinking' || !curConfig || !curParams) return '';

    setHomeEngaged(true);
    const next = [...curMessages, { role: 'user', text }];
    setHomeMessages(next);
    setHomeInput('');
    setHomePhase('thinking');

    let aiReply = '';
    const pushAi = (t) => { aiReply = t; setHomeMessages(m => [...m, { role: 'ai', text: t }]); };
    const pushAiObj = (msg) => { aiReply = msg.text || ''; setHomeMessages(m => [...m, msg]); };

    try {
      const start = next.findIndex(m => m.role === 'user');
      const initialMsgs = next.slice(start).map(m => ({
        role: m.role === 'ai' ? 'assistant' : 'user',
        content: m.text,
      }));
      const datesForCtx = await getDates().catch(() => []);
      const systemPrompt = (curConfig.homeSystemPrompt || '') + buildStateContext(curParams, curStats) + buildMemoryContext(memoryRef.current) + buildHoldingsAvailability(datesForCtx);
      const tools = [
        HOME_ADVISOR_TOOL,
        QUERY_HISTORICAL_DATA_TOOL,
        SHOW_HOLDINGS_TOOL,
        SHOW_SECTORS_TOOL,
        SHOW_PERFORMANCE_TOOL,
        ASK_MARKET_TOOL,
      ];

      // Multi-turn tool loop. The agent may call query_historical_data first,
      // receive raw rows as a tool_result, then in a follow-up turn render a
      // chart and/or markdown table via respond_to_client. Terminal tools
      // (respond_to_client / ask_market / show_*) render UI and exit the loop.
      let convo = [...initialMsgs];
      let handled = false;
      const MAX_ITER = 5;
      let lastToolUseInput = null;
      let lastToolUseName  = null;

      for (let iter = 0; iter < MAX_ITER && !handled; iter++) {
        const data = await callClaudeMulti(systemPrompt, convo, tools, CLAUDE_MODEL, 4096);
        if (data.stop_reason === 'max_tokens') {
          throw new Error('Claude hit max_tokens — response was truncated.');
        }
        const toolUses = (data.content || []).filter(c => c.type === 'tool_use');

        if (toolUses.length === 0) {
          // Model emitted text without a tool — treat as final message.
          const textBlock = (data.content || []).find(c => c.type === 'text');
          pushAi(textBlock?.text || '');
          handled = true;
          break;
        }

        // Data-fetch tools are non-terminal: run them, feed results back, loop.
        const dataToolUses = toolUses.filter(t => t.name === 'query_historical_data');
        if (dataToolUses.length > 0) {
          convo.push({ role: 'assistant', content: data.content });
          const toolResults = await Promise.all(
            toolUses.map(async (t) => {
              if (t.name === 'query_historical_data') {
                try {
                  const result = await runQueryHistoricalData(t.input);
                  return { type: 'tool_result', tool_use_id: t.id, content: JSON.stringify(result) };
                } catch (e) {
                  return { type: 'tool_result', tool_use_id: t.id, content: `Error: ${e.message}`, is_error: true };
                }
              }
              // Defensive: model called a terminal tool alongside a data
              // tool. Skip the terminal one for this turn and let the model
              // re-decide once it has the data.
              return { type: 'tool_result', tool_use_id: t.id, content: 'Skipped — wait for data result.' };
            })
          );
          convo.push({ role: 'user', content: toolResults });
          continue;
        }

        // Terminal tool. Existing legacy paths (show_*, ask_market) below
        // sometimes make their OWN follow-up callClaude with a context block
        // — those still work; this loop just routes the first terminal call.
        const t = toolUses[0];
        lastToolUseName  = t.name;
        lastToolUseInput = t.input;
        if (t.name !== 'show_performance' && t.name !== 'show_holdings' && t.name !== 'show_sectors' && t.name !== 'ask_market' && t.name !== 'respond_to_client') {
          pushAi(`⚠️ Unhandled tool: ${t.name}`);
          handled = true;
        }
        break;  // exit the data-fetch loop; legacy handler block below runs
      }

      if (handled) {
        // Already pushed via fallback / unhandled-tool branch
      } else if (lastToolUseName === 'respond_to_client') {
        const input = lastToolUseInput || {};
        const msg = { role: 'ai', text: input.message || '' };
        if (input.chart && Array.isArray(input.chart.series)) msg.chart = input.chart;
        pushAiObj(msg);
      } else if (lastToolUseName === 'ask_market') {
        const input = lastToolUseInput || {};
        try {
          const brief = buildPortfolioBrief(homeData, curParams);
          const answer = await askMarketAgent(input.question, brief);
          pushAi(answer);
        } catch (err) {
          console.error('Gemini error:', err);
          pushAi(`⚠️ Market research failed: ${err.message}`);
        }
      } else if (lastToolUseName === 'show_performance') {
        const input = lastToolUseInput || {};
        try {
          const { fromDate, toDate } = input;
          const from = new Date(fromDate), to = new Date(toDate);
          const sliced = (sleeveDataRef.current || []).filter(row => {
            const d = new Date(row.date);
            return d >= from && d <= to;
          });
          if (sliced.length < 2) {
            pushAi(`⚠️ Not enough data between ${fromDate} and ${toDate}.`);
          } else {
            const periodData  = calcBacktest(sliced, curParams);
            const periodStats = calcStats(periodData);
            setHoldingsModal({ view: 'performance', fromDate, toDate, periodData, periodStats });
            const last = periodData[periodData.length - 1];
            const stratPct = (((last.Strategy - 10000) / 10000) * 100).toFixed(1);
            const benchPct = (((last['S&P 500'] - 10000) / 10000) * 100).toFixed(1);
            const block =
              `\n\nPERIOD PERFORMANCE JUST DISPLAYED:\nFrom ${fromDate} to ${toDate}\n` +
              `Strategy: $10,000 → $${last.Strategy.toLocaleString()} (${stratPct}%, ann. ${periodStats.annualizedReturn.toFixed(1)}%)\n` +
              `S&P 500:  $10,000 → $${last['S&P 500'].toLocaleString()} (${benchPct}%)\n` +
              `Max DD: ${periodStats.maxDrawdown.toFixed(1)}% · Sharpe: ${periodStats.sharpe.toFixed(2)}\n\n` +
              `Respond by calling respond_to_client. 3–4 sentences summarising relative performance.`;
            const { input: a } = await callClaude((curConfig.homeSystemPrompt || '') + buildStateContext(curParams, curStats) + buildMemoryContext(memoryRef.current) + block, initialMsgs, [HOME_ADVISOR_TOOL]);
            pushAi(a.message);
          }
        } catch (e) { pushAi(`⚠️ ${e.message}`); }
      } else if (lastToolUseName === 'show_holdings' || lastToolUseName === 'show_sectors') {
        const input = lastToolUseInput || {};
        try {
          const isCurrent = input.isCurrent === true;
          let loadDate = input.date;
          if (isCurrent) {
            const dates = await getDates();
            loadDate = dates[dates.length - 1];
          }
          const { longs, shorts } = await loadHoldings(loadDate);
          const displayDate = isCurrent ? todayIsoLocal() : loadDate;
          const longPct  = 100 - curParams.shortPercent - curParams.goldPercent - curParams.bondsPercent;
          const shortPct = curParams.shortPercent;
          if (lastToolUseName === 'show_holdings') {
            setHoldingsModal({ view: 'holdings', date: displayDate, longs, shorts, longPct, shortPct });
            const fmt = (r, s) => `  - ${r.name} (${r.ticker}): ${(r.weight * s).toFixed(1)}%`;
            const block =
              `\n\nHOLDINGS JUST LOADED${isCurrent ? ` (presented as today's portfolio, ${displayDate})` : ` for ${displayDate}`}.\nLong (${longPct}% of portfolio):\n${longs.map(r => fmt(r, longPct)).join('\n')}\n\nShort (${shortPct}%):\n${shorts.map(r => fmt(r, shortPct)).join('\n')}\n\n` +
              `Respond by calling respond_to_client. 3–4 sentences on sector mix and positioning. ${isCurrent ? `Reference the portfolio as of today (${displayDate}); do NOT mention the underlying snapshot date.` : `Reference ${displayDate}.`} Use company names only (e.g. "McDonald's"), NEVER ticker symbols.`;
            const { input: a } = await callClaude((curConfig.homeSystemPrompt || '') + buildStateContext(curParams, curStats) + buildMemoryContext(memoryRef.current) + block, initialMsgs, [HOME_ADVISOR_TOOL]);
            pushAi(a.message);
          } else {
            const c = await classifyHoldings(longs, shorts, displayDate);
            const longSectors  = aggregateSectors(c.long_classifications  || [], longs,  longPct);
            const shortSectors = aggregateSectors(c.short_classifications || [], shorts, shortPct);
            setHoldingsModal({ view: 'sectors', date: displayDate, longSectors, shortSectors, longPct, shortPct });
            pushAi(c.message);
          }
        } catch (e) { pushAi(`⚠️ ${e.message}`); }
      }
    } catch (err) {
      console.error('Home advisor error:', err);
      pushAi('⚠️ I had trouble connecting. Please try again.');
    }
    setHomePhase('chat');
    homeInputRef.current?.focus();
    // Periodic auto-extract — every N user messages, refresh durable memory.
    homeMsgsSinceExtractRef.current++;
    if (homeMsgsSinceExtractRef.current >= EXTRACT_EVERY) {
      homeMsgsSinceExtractRef.current = 0;
      // Read latest messages by appending the just-pushed AI reply.
      const latest = [...next, { role: 'ai', text: aiReply }];
      autoExtractMemory(latest, 'home');
    }
    return aiReply;
  }

  function homeSend() { homeSendMessage(homeInput.trim(), homeMessages, homePhase, params, stats, config); }
  function homeSendDirect(text) { homeSendMessage(text, homeMessages, homePhase, params, stats, config); }
  function homeHandleKey(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); homeSend(); } }

  const inputPlaceholder = params
    ? 'Ask to adjust — e.g. "increase gross to 140" or "add more gold"…'
    : 'Type your answer…';

  function closeHoldings() { setHoldingsModal(null); }

  // Fetches today's intraday CSV from the api-proxy Lambda (which fans out
  // to Twelve Data) and computes each holding's contribution to today's
  // portfolio performance: (stock_return × sleeve_weight × gross),
  // sign-flipped for shorts. Sorted descending. Used by the Drivers chart.
  async function fetchDrivers() {
    if (!homeData || !params) throw new Error('Portfolio snapshot is still loading.');

    const res = await fetch('/intraday_today.csv?t=' + Date.now());
    if (!res.ok) throw new Error(`Intraday CSV unavailable (HTTP ${res.status}). The updater Lambda may not have run yet today.`);
    const text = await res.text();
    const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
    const rows = parsed.data || [];
    if (rows.length === 0) return [];

    const tickerCols = Object.keys(rows[0]).filter(c => c && c !== 'timestamp');

    // The updater prepends a single `prior_close` row (yesterday's close per
    // ticker) as the overnight baseline. Fall back to today's first bar if
    // it isn't available for a given ticker.
    const priorCloseRow = rows.find(r => r.timestamp === 'prior_close') || null;
    const intradayRows  = rows.filter(r => r.timestamp !== 'prior_close');

    const open = {}, current = {};
    for (const col of tickerCols) {
      if (priorCloseRow) {
        const v = parseFloat(priorCloseRow[col]);
        if (isFinite(v) && v > 0) open[col] = v;
      }
      if (open[col] == null) {
        for (const row of intradayRows) {
          const v = parseFloat(row[col]);
          if (isFinite(v) && v > 0) { open[col] = v; break; }
        }
      }
      for (let i = intradayRows.length - 1; i >= 0; i--) {
        const v = parseFloat(intradayRows[i][col]);
        if (isFinite(v) && v > 0) { current[col] = v; break; }
      }
    }

    const gross = params.grossExposure / 100;
    const sp    = params.shortPercent  / 100;
    const gp    = params.goldPercent   / 100;
    const bp    = params.bondsPercent  / 100;
    const lp    = 1 - sp - gp - bp;

    const contributionFor = (h, sleeveWeight, sign) => {
      const o = open[h.ticker], c = current[h.ticker];
      if (!isFinite(o) || o <= 0 || !isFinite(c) || c <= 0) return null;
      const stockReturn = c / o - 1;
      return {
        ticker: h.ticker,
        name:   h.name,
        side:   sign > 0 ? 'long' : 'short',
        stockReturnPct: stockReturn * 100,
        // Contribution = gross × sleeve_weight × stock_weight × stockReturn,
        // with sign flipped for shorts (rising stock hurts a short position).
        contribution: sign * gross * sleeveWeight * h.weight * stockReturn * 100,
      };
    };

    const drivers = [];
    for (const h of homeData.longs)  { const d = contributionFor(h, lp,  +1); if (d) drivers.push(d); }
    for (const h of homeData.shorts) { const d = contributionFor(h, sp, -1); if (d) drivers.push(d); }

    drivers.sort((a, b) => b.contribution - a.contribution);
    return drivers;
  }

  // Generates (and caches) a 2–3 sentence description of the portfolio's
  // overall style and positioning. Keyed on (params + holdings) so it only
  // re-runs when the deployed portfolio changes.
  async function fetchStyleBrief() {
    if (!homeData || !params) throw new Error('Portfolio snapshot is still loading.');
    const key = portfolioSignature(homeData, params);
    const cur = styleBriefRef.current;
    if (cur.text && cur.key === key) return cur.text;
    if (cur.loading && cur.key === key) return '';
    styleBriefRef.current = { text: '', key, loading: true };
    setStyleBrief({ text: '', key, loading: true, error: '' });
    try {
      const brief = buildPortfolioBrief(homeData, params);
      const system =
        'You describe the style and positioning of an investment portfolio in 1–2 tight plain-text sentences for a wealth-management UI. ' +
        'Cover (as compactly as possible): overall character (defensive vs. cyclical, balanced vs. concentrated), the biggest sector tilts (e.g. "leans toward health care, away from real estate"), and anything notable. ' +
        'Keep total length under 300 characters. No preamble, no lists, no disclaimers — just direct prose.\n\n' +
        RETAIL_LANGUAGE_GUIDE;
      const user = `Current portfolio:\n\n${brief}\n\nWrite the 1–2 sentence description now. Under 300 characters.`;
      const text = (await callClaude(system, [{ role: 'user', content: user }])).trim();
      styleBriefRef.current = { text, key, loading: false };
      setStyleBrief({ text, key, loading: false, error: '' });
      return text;
    } catch (e) {
      styleBriefRef.current = { text: '', key, loading: false };
      setStyleBrief({ text: '', key, loading: false, error: e?.message || 'Could not generate portfolio description.' });
      throw e;
    }
  }

  // Generates (and caches) a 2–3 sentence "since we last spoke" continuity
  // briefing for returning users. Cached on (memory.lastSession + portfolio
  // signature + drivers signature). Only meaningful when memory is present.
  async function fetchSinceWeSpokeBrief() {
    if (!homeData || !params) throw new Error('Portfolio snapshot is still loading.');
    const m = memoryRef.current;
    if (!m) throw new Error('No prior memory — this is a cold-start session.');

    let drivers = [];
    try { drivers = await fetchDrivers(); } catch {}
    const driversKey = driversSignature(drivers);
    const key = `${m.lastSession || ''}||${portfolioSignature(homeData, params)}||${driversKey}`;
    const cur = sinceWeSpokeBriefRef.current;
    if (cur.text && cur.key === key) return cur.text;
    if (cur.loading && cur.key === key) return '';

    sinceWeSpokeBriefRef.current = { text: '', key, loading: true };
    setSinceWeSpokeBrief({ text: '', key, loading: true, error: '' });

    try {
      let driverSummary = '';
      if (drivers.length) {
        const total = drivers.reduce((a, d) => a + d.contribution, 0);
        const top = drivers.slice(0, 3);
        const bot = [...drivers].slice(-3).reverse();
        const line = (d) => `${d.name} (${d.contribution >= 0 ? '+' : ''}${d.contribution.toFixed(2)}%)`;
        driverSummary =
          `Today's portfolio P&L so far: ${total >= 0 ? '+' : ''}${total.toFixed(2)}%.\n` +
          `Top contributors today: ${top.map(line).join(', ')}.\n` +
          `Biggest detractors today: ${bot.map(line).join(', ')}.`;
      }
      const memSnippet = buildMemoryContext(m);
      const system =
        'You write a 2–3 sentence continuity briefing for a returning client of Puro Wealth. The client just opened the app — they have history with us. Pick the most relevant connection between what was discussed in prior sessions (concerns, follow-ups, holdings of interest) and what has happened in the portfolio since. Be concrete: name a holding, a number, or an event. Don\'t greet generically; you are continuing a relationship. No preamble, no disclaimers — direct prose only.\n\n' +
        RETAIL_LANGUAGE_GUIDE;
      const user =
        `${memSnippet}\n\n` +
        (driverSummary ? `TODAY'S PORTFOLIO ACTIVITY:\n${driverSummary}\n\n` : '') +
        `Write the 2–3 sentence "since we spoke" briefing now.`;
      const text = (await callClaude(system, [{ role: 'user', content: user }])).trim();
      sinceWeSpokeBriefRef.current = { text, key, loading: false };
      setSinceWeSpokeBrief({ text, key, loading: false, error: '' });
      return text;
    } catch (e) {
      sinceWeSpokeBriefRef.current = { text: '', key, loading: false };
      setSinceWeSpokeBrief({ text: '', key, loading: false, error: e?.message || 'Could not generate continuity brief.' });
      throw e;
    }
  }

  // Generates (and caches) a 2–3 sentence commentary on what is driving the
  // portfolio today. Keyed on the drivers signature so it refreshes with each
  // intraday CSV pull.
  async function fetchTodayBrief() {
    const drivers = await fetchDrivers();
    const key = driversSignature(drivers);
    const cur = todayBriefRef.current;
    if (cur.text && cur.key === key) return { text: cur.text, drivers };
    if (cur.loading && cur.key === key) return { text: '', drivers };
    todayBriefRef.current = { text: '', key, loading: true };
    setTodayBrief({ text: '', key, loading: true, error: '' });
    try {
      if (!drivers.length) {
        const empty = 'No intraday data yet — waiting for the first prices of the session.';
        todayBriefRef.current = { text: empty, key, loading: false };
        setTodayBrief({ text: empty, key, loading: false, error: '' });
        return { text: empty, drivers };
      }
      const total = drivers.reduce((a, d) => a + d.contribution, 0);
      const top = drivers.slice(0, 4);
      const bot = [...drivers].slice(-4).reverse();
      const line = (d) =>
        `${d.name} (${d.side}, stock ${d.stockReturnPct >= 0 ? '+' : ''}${d.stockReturnPct.toFixed(2)}%, ` +
        `contribution ${d.contribution >= 0 ? '+' : ''}${d.contribution.toFixed(3)}%)`;
      const context =
        `Portfolio return so far today: ${total >= 0 ? '+' : ''}${total.toFixed(2)}%\n\n` +
        `Top contributors:\n  ${top.map(line).join('\n  ')}\n\n` +
        `Biggest detractors:\n  ${bot.map(line).join('\n  ')}`;
      const system =
        'You write a 1–2 sentence commentary on what is driving a client portfolio today, for a wealth-management UI. ' +
        'Focus on the portfolio total and the 1–2 most influential names (or sectors if a theme is obvious). ' +
        'No preamble, no hedging, no disclaimers — just direct prose.\n\n' +
        RETAIL_LANGUAGE_GUIDE;
      const user = `Today's driver data:\n\n${context}\n\nWrite the 1–2 sentence commentary now.`;
      const text = (await callClaude(system, [{ role: 'user', content: user }])).trim();
      todayBriefRef.current = { text, key, loading: false };
      setTodayBrief({ text, key, loading: false, error: '' });
      return { text, drivers };
    } catch (e) {
      todayBriefRef.current = { text: '', key, loading: false };
      setTodayBrief({ text: '', key, loading: false, error: e?.message || 'Could not generate today commentary.' });
      throw e;
    }
  }

  // ── Markets & Context: Finnhub headlines + Claude impact analysis ──────────
  async function fetchHeadlines() {
    const cur = marketsHeadlinesRef.current;
    if (cur.items.length > 0) return cur.items;            // session cache
    if (headlinesPromiseRef.current) return headlinesPromiseRef.current; // share in-flight

    const p = (async () => {
      marketsHeadlinesRef.current = { items: [], loading: true, error: '' };
      setMarketsHeadlines({ items: [], loading: true, error: '' });
      try {
        // Routed through the api-proxy Lambda which injects the token
        // server-side (FINNHUB_API_KEY env var sourced from Doppler).
        const res = await apiFetch('/api/finnhub/news?category=general');
        if (!res.ok) throw new Error(`Finnhub ${res.status}: ${await res.text()}`);
        const all = await res.json();
        // Finnhub sometimes returns 200 with a JSON error envelope
        // (rate-limit, key issue, etc.) instead of an article array. Surface
        // that instead of silently rendering a blank list.
        if (!Array.isArray(all)) {
          const msg = all?.error || all?.message || `Finnhub returned an unexpected response: ${JSON.stringify(all).slice(0, 200)}`;
          throw new Error(msg);
        }
        const seen = new Set();
        const items = all
          .filter(it => it && it.headline && it.id)
          .filter(it => {
            const key = it.headline.toLowerCase().trim();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 12);
        marketsHeadlinesRef.current = { items, loading: false, error: '' };
        setMarketsHeadlines({ items, loading: false, error: '' });
        return items;
      } catch (e) {
        const err = { items: [], loading: false, error: e?.message || 'Could not load news.' };
        marketsHeadlinesRef.current = err;
        setMarketsHeadlines(err);
        throw e;
      } finally {
        headlinesPromiseRef.current = null;
      }
    })();
    headlinesPromiseRef.current = p;
    return p;
  }

  async function analyzeHeadline(item) {
    if (!homeData || !params) throw new Error('Portfolio snapshot is still loading.');
    const id = item.id;
    const cached = marketsAnalysisRef.current[id];
    if (cached?.summary && cached?.impact) return cached;
    if (cached?.loading) return cached;

    const pending = { summary: '', impact: '', loading: true, error: '' };
    marketsAnalysisRef.current = { ...marketsAnalysisRef.current, [id]: pending };
    setMarketsAnalysis({ ...marketsAnalysisRef.current });

    try {
      const brief = buildPortfolioBrief(homeData, params);
      const system =
        `You are a market-news analyst for a wealth-management client. Today's date is ${todayIsoLocal()}.\n\n` +
        `The client's current portfolio:\n\n${brief}\n\n` +
        `${RETAIL_LANGUAGE_GUIDE}\n\n` +
        `Call analyze_news_impact. The summary should be a neutral digest of the article. The impact should be specific and concrete — name the affected holdings and say whether the story is positive, negative, or neutral for them. If the story has no meaningful connection to any holding, say so in one sentence.`;
      const articleBody = item.summary || '(No article body available — analyze from the headline alone.)';
      const user =
        `HEADLINE: ${item.headline}\n` +
        `SOURCE: ${item.source || 'unknown'}\n\n` +
        `ARTICLE:\n${articleBody}\n\n` +
        `Provide the summary and portfolio-impact analysis now.`;
      const { input } = await callClaude(
        system,
        [{ role: 'user', content: user }],
        [ANALYZE_NEWS_TOOL],
        CLAUDE_MODEL_FAST,
      );
      const result = { summary: input.summary, impact: input.impact, loading: false, error: '' };
      marketsAnalysisRef.current = { ...marketsAnalysisRef.current, [id]: result };
      setMarketsAnalysis({ ...marketsAnalysisRef.current });
      return result;
    } catch (e) {
      const err = { summary: '', impact: '', loading: false, error: e?.message || 'Analysis failed.' };
      marketsAnalysisRef.current = { ...marketsAnalysisRef.current, [id]: err };
      setMarketsAnalysis({ ...marketsAnalysisRef.current });
      throw e;
    }
  }

  // Synthesize today's headlines + portfolio into themed drivers. Cached on
  // (headline IDs + portfolio signature) so it only re-runs when either changes.
  async function fetchThemes() {
    if (!homeData || !params) throw new Error('Portfolio snapshot is still loading.');

    // Cheap pre-check: if we already have themes for the current headline set
    // + portfolio, return them without disturbing state.
    const cachedItems = marketsHeadlinesRef.current.items;
    if (cachedItems.length) {
      const idsKey = cachedItems.map(it => it.id).sort((a, b) => a - b).join(',');
      const maybeKey = `${portfolioSignature(homeData, params)}||${idsKey}`;
      const cur = marketsThemesRef.current;
      if (cur.items.length && cur.key === maybeKey && !cur.loading) return cur.items;
      if (cur.loading && cur.key === maybeKey) return [];
    }

    // Flip UI into "loading" IMMEDIATELY so the user sees progress even while
    // we're still awaiting Finnhub. Any failure below will overwrite this with
    // a proper error state.
    marketsThemesRef.current = { items: [], key: 'pending', loading: true, error: '' };
    setMarketsThemes({ items: [], key: 'pending', loading: true, error: '' });

    try {
      let items = marketsHeadlinesRef.current.items;
      if (!items.length) items = await fetchHeadlines();
      if (!items || !items.length) throw new Error('No headlines available yet.');

      const idsKey = items.map(it => it.id).sort((a, b) => a - b).join(',');
      const key = `${portfolioSignature(homeData, params)}||${idsKey}`;

      const brief = buildPortfolioBrief(homeData, params);
      const headlinesBlock = items.map(it =>
        `[id=${it.id}] ${it.headline}\n  ${(it.summary || '').slice(0, 300)}`
      ).join('\n\n');
      const system =
        `You are synthesizing today's U.S. market news flow for a wealth-management client and distilling recurring or high-impact themes. Today's date is ${todayIsoLocal()}.\n\n` +
        `CLIENT PORTFOLIO:\n\n${brief}\n\n` +
        `${RETAIL_LANGUAGE_GUIDE}\n\n` +
        `Call analyze_portfolio_themes with 6–10 themes. Prefer cross-cutting narratives (e.g. "Rising oil prices", "Fed policy easing", "AI capex boom") over single-headline restatements. Each theme must identify positive vs. negative for THIS portfolio (not for the market generally) and be bucketed as major or minor impact. Intensity (0–1) sizes the pill in the UI — reserve values above 0.8 for truly consequential themes. Rationale must be retail-friendly (apply the language style above) and reference affected holdings by company name. linkedHeadlineIds must come from the numeric IDs in the headline list below.`;
      const user = `TODAY'S HEADLINES:\n\n${headlinesBlock}\n\nSynthesize now.`;
      const { input } = await callClaude(
        system,
        [{ role: 'user', content: user }],
        [ANALYZE_THEMES_TOOL],
        CLAUDE_MODEL,  // Sonnet 4.6 — this is a synthesis task, not pure summarization
        4096,          // 8 themes with rationale + linked IDs easily exceeds the 1024 default
      );
      const themes = (input?.themes || []).filter(t => t && t.name);
      if (!themes.length) {
        // Empty / malformed tool output — treat as error so the effect doesn't
        // spin in a loop of "items=0, no error" → retry → empty → retry.
        throw new Error('Synthesizer returned no themes. Try again.');
      }
      marketsThemesRef.current = { items: themes, key, loading: false, error: '' };
      setMarketsThemes({ items: themes, key, loading: false, error: '' });
      return themes;
    } catch (e) {
      const err = { items: [], key: 'pending', loading: false, error: e?.message || 'Could not synthesize themes.' };
      marketsThemesRef.current = err;
      setMarketsThemes(err);
      throw e;
    }
  }

  async function fetchIntraday() {
    if (!homeData || !params) throw new Error('Portfolio snapshot is still loading.');

    const res = await fetch('/intraday_today.csv?t=' + Date.now());
    if (!res.ok) throw new Error(`Intraday CSV unavailable (HTTP ${res.status}). The updater Lambda may not have run yet today.`);
    const text = await res.text();
    const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
    const rows = parsed.data || [];
    if (rows.length === 0) {
      // CSV is completely empty / unparseable. Surface as an error so the
      // UI shows a Retry button rather than the ambiguous "no data" state.
      throw new Error('Intraday CSV is empty. Try again in a minute.');
    }

    // If the CSV only has the prior_close row, the updater rotated the file
    // for today but hasn't fetched the first intraday tick yet. Common in
    // the first 1-2 minutes after market open. Throw a distinctive,
    // user-friendly error so the empty-state isn't ambiguous.
    const hasIntradayRows = rows.some(r => r.timestamp && r.timestamp !== 'prior_close');
    if (!hasIntradayRows) {
      throw new Error("Waiting for today's first intraday tick — the updater runs a few minutes after market open. Try again shortly.");
    }

    const tickerCols = Object.keys(rows[0]).filter(c => c && c !== 'timestamp');

    // Prior-close row (yesterday's close per ticker) is the overnight baseline.
    // Intraday rows carry real timestamps; drop prior_close from the chart series.
    const priorCloseRow = rows.find(r => r.timestamp === 'prior_close') || null;
    const intradayRows  = rows.filter(r => r.timestamp !== 'prior_close');

    // Baseline for each ticker: prefer prior close so P&L includes the
    // overnight gap. Fall back to today's first valid bar if missing.
    const open = {};
    for (const col of tickerCols) {
      if (priorCloseRow) {
        const v = parseFloat(priorCloseRow[col]);
        if (isFinite(v) && v > 0) open[col] = v;
      }
      if (open[col] == null) {
        for (const row of intradayRows) {
          const v = parseFloat(row[col]);
          if (isFinite(v) && v > 0) { open[col] = v; break; }
        }
      }
    }

    const gross = params.grossExposure / 100;
    const sp    = params.shortPercent  / 100;
    const gp    = params.goldPercent   / 100;
    const bp    = params.bondsPercent  / 100;
    const lp    = 1 - sp - gp - bp;

    // Sleeve return: weighted average of % moves across holdings. Originally
    // this returned null whenever ANY ticker had a missing price, which
    // killed the whole strategy line if the Twelve Data updater dropped
    // even one symbol. Now we sum over the tickers we have prices for and
    // re-normalize by their covered weight so a couple of missing tickers
    // just give us a slightly thinner sample, not a null. Returns null
    // only if coverage falls below 50% (then the number is too unreliable
    // to display).
    const sleeveReturn = (row, holdings) => {
      let acc = 0;
      let coveredWeight = 0;
      let totalWeight = 0;
      for (const h of holdings) {
        totalWeight += h.weight;
        const pNow  = parseFloat(row[h.ticker]);
        const pOpen = open[h.ticker];
        if (!isFinite(pNow) || pNow <= 0 || !isFinite(pOpen) || pOpen <= 0) continue;
        acc += h.weight * (pNow / pOpen - 1);
        coveredWeight += h.weight;
      }
      if (totalWeight <= 0 || coveredWeight / totalWeight < 0.5) return null;
      return acc * (totalWeight / coveredWeight);
    };

    // One-time coverage diagnostic: surface which tickers are missing from
    // the intraday CSV so the updater script's failure mode is visible.
    {
      const allHoldings = [...(homeData.longs || []), ...(homeData.shorts || [])];
      const missing = allHoldings
        .map(h => h.ticker)
        .filter(t => !isFinite(open[t]) || open[t] <= 0);
      if (missing.length > 0) {
        console.warn('[intraday] %d/%d tickers missing prior_close — strategy line will use partial coverage:', missing.length, allHoldings.length, missing);
      }
    }

    return intradayRows.map(row => {
      const ts = String(row.timestamp || '');
      const m  = ts.match(/T(\d{2}):(\d{2})/);
      if (!m) return null;
      const time = `${m[1]}:${m[2]}`;

      const spyNow  = parseFloat(row.SPY);
      const spyOpen = open.SPY;
      const spPct = (isFinite(spyNow) && spyNow > 0 && isFinite(spyOpen) && spyOpen > 0)
        ? (spyNow / spyOpen - 1) * 100
        : null;

      const longR  = sleeveReturn(row, homeData.longs);
      const shortR = sleeveReturn(row, homeData.shorts);
      // Short sleeve flips sign: its return is positive when shorted stocks fall.
      const stratPct = (longR != null && shortR != null)
        ? gross * (lp * longR + sp * (-shortR)) * 100
        : null;

      return { time, spPct, stratPct };
    }).filter(Boolean);
  }

  // Fetch (and cache) a short company description for the given ticker/name.
  // Returns the description text. Cache persists for the session.
  async function describeCompany(ticker, name) {
    if (descCacheRef.current[ticker]) return descCacheRef.current[ticker];
    const system = 'You provide very short company descriptions for a portfolio UI. Respond with exactly 1–2 plain sentences describing what the company does and its main area of business. No preamble, no ticker, no quotes, no financial metrics — just the business description.';
    try {
      const text = await callClaude(system, [{ role: 'user', content: `Describe ${name} (${ticker}) in 1–2 sentences — focus on what they do.` }]);
      const clean = (text || '').trim();
      descCacheRef.current = { ...descCacheRef.current, [ticker]: clean };
      setDescCache(descCacheRef.current);
      return clean;
    } catch (err) {
      console.error('describeCompany error:', err);
      const fallback = 'Description unavailable.';
      descCacheRef.current = { ...descCacheRef.current, [ticker]: fallback };
      setDescCache(descCacheRef.current);
      return fallback;
    }
  }

  const value = {
    // strategy state
    config, messages, input, phase, params, backtestData, stats,
    // Risk-profile dial + tilt sliders (primary Strategy controls)
    riskProfiles: RISK_PROFILES,
    riskProfileKeys: PROFILE_KEYS,
    activeProfile, profileTilts,
    selectProfile, setTilt, setRawParams,
    holdingsModal,
    // home state
    deployed, allocationUsd, homeMessages, homeInput, homePhase, homeData,
    // Persistent memory (returning-user continuity)
    memory,
    isReturningUser: !!memory,
    memoryViewOpen,
    metricsViewOpen,
    resetMemory, seedMemory: seedMemoryNow, openMemoryView, closeMemoryView,
    openMetricsView, closeMetricsView,
    // per-chat engagement flags (for cold-start suggestion chips)
    strategyEngaged, homeEngaged,
    // voice state
    voiceActivePage,
    voiceState,                             // 'idle' | 'listening' | 'speaking' — Strategy/Home browser voice
    voiceTranscript, isBootstrapping, bootstrapError,
    // HeyGen LiveAvatar (onboarding)
    liveAvatarToken, liveAvatarLoading, liveAvatarError,
    // HeyGen LiveAvatar (Strategy/Home chat — hybrid)
    chatLiveAvatarToken,
    registerChatAvatar, unregisterChatAvatar,
    notifyChatAvatarReady, notifyChatAvatarSpeakStarted, notifyChatAvatarSpeakEnded,
    advisors:           ADVISORS,
    selectedAdvisorIdx,
    selectedAdvisor:    ADVISORS[selectedAdvisorIdx] || ADVISORS[0],
    advisorPreviewUrl:  advisorPreviews[(ADVISORS[selectedAdvisorIdx] || ADVISORS[0])?.avatarId] || '',
    advisorPreviews,
    selectAdvisor:      (idx) => setSelectedAdvisorIdx(((idx % ADVISORS.length) + ADVISORS.length) % ADVISORS.length),
    nextAdvisor:        () => setSelectedAdvisorIdx((i) => (i + 1) % ADVISORS.length),
    prevAdvisor:        () => setSelectedAdvisorIdx((i) => (i - 1 + ADVISORS.length) % ADVISORS.length),
    recordOnboardingMessage, finalizeOnboardingTranscript,
    // refs
    bottomRef, inputRef, homeBottomRef, homeInputRef,
    // handlers
    setInput, send, sendDirect, handleKey, deploy, resetConversation,
    setHomeInput, homeSend, homeSendDirect, homeHandleKey,
    closeHoldings, bootstrapFromTranscript, bypassOnboarding, resumeFromMemory,
    describeCompany, descCache,
    fetchIntraday, fetchDrivers,
    styleBrief, todayBrief, sinceWeSpokeBrief,
    fetchStyleBrief, fetchTodayBrief, fetchSinceWeSpokeBrief,
    marketsHeadlines, marketsAnalysis, selectedHeadlineId,
    fetchHeadlines, analyzeHeadline, setSelectedHeadlineId,
    marketsThemes, marketsView, fetchThemes, setMarketsView,
    startOnboardingVoice, startStrategyVoice, startHomeVoice, stopVoice,
    // derived
    inputPlaceholder,
  };

  return <AdvisorContext.Provider value={value}>{children}</AdvisorContext.Provider>;
}
